/* Average Joe's Golf — fitting autofill for MageWorx Advanced Product Options
 *
 * Written against the real APO markup on averagejoesgolf.com:
 *   .mw-apo-container
 *     .mw-option[data-option-id="o_3500533"]
 *       label.mw-label > .mw-text--style-strong   ← the option title
 *       select.mw-option__control[name="mw_opts[o_...]"]
 *         option[value="v_..."]                   ← the option value
 *
 * APO renders after the rest of the page, so this waits for it rather than
 * running once.
 *
 * INSTALL (Shopify):
 *   Admin → Content → Files → upload this file, then add to the product
 *   template, near the end of <body>:
 *     <script src="{{ 'fitting-autofill.js' | asset_url }}" defer></script>
 *
 * TUNING: add ?ajgdebug=1 to a product URL and read the console. It lists
 * every APO option it found, what it matched, and what it could not.
 *
 * REQUIREMENT: the fitting guide must be served from averagejoesgolf.com.
 * See the notes at the foot of this file.
 */
(function () {
  "use strict";

  var KEY = "ajg_fitting";
  var FITTING_PAGE = "/pages/free-online-golf-club-fitting";
  // same page with the theme chrome stripped, for the pop-up
  var FITTING_EMBED = "/pages/free-online-golf-club-fitting?view=embed";
  var ACCENT = "#c8102e", INK = "#2b211c", CREAM = "#fdf3e7", LINE = "#eadcc8";
  var DEBUG = /[?&]ajgdebug=1/.test(location.search);
  var WATCH_MS = 25000, SETTLE_MS = 300;

  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ["[ajg]"].concat(Array.prototype.slice.call(arguments)));
  }

  /* ---------- storage ---------- */

  function parse(raw) {
    if (!raw) return null;
    try { var d = JSON.parse(raw); return d && d.clubs ? d : null; } catch (e) { return null; }
  }

  /* Two stores, and they can disagree. The cookie is small and reaches the
   * server; localStorage holds the fuller record. A cookie that failed to
   * write (over ~4 KB) leaves a STALE cookie behind while localStorage is
   * current, so prefer whichever record is newer, then whichever is richer. */
  function readSaved() {
    var fromCookie = null, fromLocal = null;
    try {
      var m = document.cookie.match(new RegExp("(?:^|; )" + KEY + "=([^;]*)"));
      if (m) fromCookie = parse(decodeURIComponent(m[1]));
    } catch (e) {}
    try { fromLocal = parse(window.localStorage.getItem(KEY)); } catch (e) {}

    if (!fromCookie) { log("source: localStorage only"); return fromLocal; }
    if (!fromLocal) { log("source: cookie only"); return fromCookie; }

    var tc = Date.parse(fromCookie.saved || "") || 0;
    var tl = Date.parse(fromLocal.saved || "") || 0;
    if (tc && tl && Math.abs(tc - tl) > 2000) {
      log("source: newer of the two", tl > tc ? "(localStorage)" : "(cookie)");
      return tl > tc ? fromLocal : fromCookie;
    }
    // same save: localStorage carries the prose as well as the numbers
    log("source: localStorage (same save, fuller record)");
    return fromLocal;
  }

  function writeBoth(obj) {
    var json = JSON.stringify(obj);
    try {
      document.cookie = KEY + "=" + encodeURIComponent(json) +
        "; path=/; Max-Age=" + (60 * 60 * 24 * 180) + "; SameSite=Lax";
    } catch (e) {}
    try { window.localStorage.setItem(KEY, json); } catch (e) {}
  }

  /* A wrong stance is usually a slip on one dropdown. Rather than make them
   * clear the fitting and start over, flip the stance in the saved record -
   * every club, since a golfer has one stance - and re-run the fill. */
  /* Names the product the way a person would: one driver, a set of irons. A
   * chipper sold in several lofts is "these chippers"; one loft is "this". */
  function stanceSubject(ck) {
    var hay = [document.title, (document.querySelector("h1") || {}).textContent || "", location.pathname].join(" ").toLowerCase();
    if (ck === "putter" && /chipper/.test(hay)) {
      var root = optionsRoot() || document, lofts = 0;
      Array.prototype.forEach.call(root.querySelectorAll(".mw-option, .mw-option__container"), function (b) {
        var m = classify(titleOf(b)), sel = b.querySelector("select");
        if (m && m.id === "loft" && sel) lofts = Math.max(lofts, usable(sel).length);
      });
      return lofts > 1 ? "These chippers are" : "This chipper is";
    }
    return ({
      irons: "These irons are",
      driver: "This driver is",
      wood: "This fairway wood is",
      hybrid: "These hybrids are",
      wedge: "These wedges are",
      putter: "This putter is"
    })[ck] || "This club is";
  }

  function switchStance(to) {
    var d = readSaved();
    if (!d || !d.clubs) return;
    var label = to === "left" ? "Left-Handed" : "Right-Handed";
    d.hand = to;
    Object.keys(d.clubs).forEach(function (k) {
      var c = d.clubs[k];
      if (c.fit) c.fit.hand = to;
      if (c.specs && c.specs["Golf stance"]) c.specs["Golf stance"] = label;
    });
    writeBoth(d);
    log("stance switched to", to);
    location.reload();
  }

  function clubKeyFor(club) {
    return (club && club.key) || pageClub() || "";
  }

  function clearSaved(onlyClub) {
    if (onlyClub) {
      var d = readSaved();
      if (d && d.clubs && d.clubs[onlyClub]) {
        delete d.clubs[onlyClub];
        if (Object.keys(d.clubs).length) {
          log("cleared", onlyClub, "and kept", Object.keys(d.clubs).join(", "));
          writeBoth(d);
          return;
        }
      }
    }
    try { document.cookie = KEY + "=; path=/; Max-Age=0; SameSite=Lax"; } catch (e) {}
    try { window.localStorage.removeItem(KEY); } catch (e) {}
  }

  /* ---------- which club is this page? ---------- */

  var CLUB_NOUN = {
    driver: "driver", wood: "fairway wood", hybrid: "hybrid",
    irons: "iron", wedge: "wedge", putter: "putter"
  };

  var CLUB_WORDS = {
    driver: ["driver", "thriver"],
    wood: ["fairway", "wood"],
    hybrid: ["hybrid", "rescue"],
    irons: ["iron"],
    wedge: ["wedge"],
    putter: ["putter", "chipper"]
  };

  /* Work out what KIND of club this page sells, independent of what the
   * customer was fitted for. Getting this backwards once meant a
   * driver-only fitting was applied to an irons page. */
  function isChipperPage() {
    var hay = [document.title, (document.querySelector("h1") || {}).textContent || "", location.pathname]
      .join(" ").toLowerCase();
    return hay.indexOf("chipper") >= 0;
  }

  function pageClub() {
    var hay = [document.title, (document.querySelector("h1") || {}).textContent || "", location.pathname]
      .join(" ").toLowerCase();
    // most specific first, so "iron" cannot swallow a wedge page
    var order = ["putter", "wedge", "hybrid", "irons", "wood", "driver"];
    for (var i = 0; i < order.length; i++) {
      var k = order[i];
      for (var j = 0; j < CLUB_WORDS[k].length; j++) {
        if (hay.indexOf(CLUB_WORDS[k][j]) >= 0) return k;
      }
    }
    return null;
  }

  // Mini Drivers and the Acer XV Thriver are built from 43", not 45"
  var MINI_STD = 43;
  function isMiniPage() {
    var hay = [document.title, (document.querySelector("h1") || {}).textContent || "", location.pathname]
      .join(" ").toLowerCase();
    return /mini[\s-]*driver|thriver/.test(hay);
  }

  function detectClub(data) {
    var page = pageClub();
    if (page) {
      if (data.clubs[page]) { log("club:", page); return page; }
      // the page is a club we can name, but this customer was not fitted
      // for it - never substitute another club's specs
      log("page is", page, "but no fitting saved for it");
      return null;
    }
    // could not identify the page at all; a single-club fitting is a safe guess
    var keys = Object.keys(data.clubs);
    if (keys.length === 1) { log("page type unknown; using only saved club:", keys[0]); return keys[0]; }
    log("page type unknown and several clubs saved; leaving the page alone");
    return null;
  }

  /* ---------- text helpers ---------- */

  // display text: the option's own wording, minus the price suffix
  function cleanValue(t) {
    return String(t || "")
      .replace(/\(\s*[+-]?\s*\$[0-9.,]+\s*\)/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function cleanLabel(t) {
    return String(t || "").replace(/\*/g, "").replace(/\s+/g, " ").trim();
  }

  function norm(s) {
    return String(s || "")
      .toLowerCase()
      .replace(/\u2033|\u2032|''/g, '"')
      .replace(/\u00b0/g, "deg")
      .replace(/\u2212|\u2013|\u2014/g, "-")
      .replace(/\u00bd/g, ".5").replace(/\u00bc/g, ".25").replace(/\u00be/g, ".75")
      .replace(/\s+/g, " ")
      .trim();
  }

  function tight(s) { return norm(s).replace(/[^a-z0-9.+-]/g, ""); }

  // first number in a string: '45" (Standard)' → 45, '10.5°' → 10.5
  function num(s) {
    var m = norm(s).match(/-?(\d*\.\d+|\d+)/);
    return m ? parseFloat(m[0]) : null;
  }

  /* ---------- option title → what we know ---------- */
  /* Titles seen in the APO data: Stance, Loft, Shaft Flex, Shaft Selection,
   * Driver Length, Grip Style, Grip Size, Grip Color, Shaft Spine Alignment?,
   * Add Head Cover?. Iron and wedge pages add Lie / Bounce equivalents. */

  var MATCHERS = [
    { id: "length", test: /length/,            kind: "nearestNumber" },
    { id: "loft",   test: /loft/,              kind: "nearestNumber" },
    { id: "lie",    test: /lie/,               kind: "text" },
    { id: "flex",   test: /flex/,              kind: "text" },
    { id: "shaft",  test: /shaft.*(select|model|option)|^shaft$/, kind: "text" },
    { id: "grip",   test: /grip\s*size/,       kind: "text" },
    { id: "bounce", test: /bounce/,            kind: "text" },
    { id: "head",   test: /head\s*(style|shape|type)/, kind: "text" },
    { id: "hand",   test: /stance|dexterit|handed/, kind: "text" },
    // a comfort build the fitting asked for, not an upsell we invented
    { id: "inserts", test: /shock\s*absorb|pro-?soft|insert/, kind: "text" },
    // the fitting now names a grip model, so that option is fair game
    { id: "gripModel", test: /grip\s*(style|model|type)/, kind: "text" }
  ];

  // titles we must never touch, even if a word above appears in them
  var SKIP = /spine|head\s*cover|grip\s*colou?r|shaft\s*(band|label)|engrav|ferrule|extra|upgrade|tolerance|assembly\s*option/;

  function classify(title) {
    var n = norm(title).replace(/\*/g, "").trim();
    if (SKIP.test(n)) return null;
    for (var i = 0; i < MATCHERS.length; i++) {
      if (MATCHERS[i].test.test(n)) return MATCHERS[i];
    }
    return null;
  }

  /* ---------- what value do we want for each? ---------- */

  /* our flex names → the words this shop's option lists use.
   * Senior, Lite, A-Flex and Amateur Flex are all the same flex. Ladies is
   * softer than all of them and is never a substitute.
   * NOTE: Extra Stiff does NOT fall back to Stiff — irons stock X-flex and
   * woods will too, so a wrong-but-close pick would be worse than none. When
   * no X option exists the field is left alone and the customer sees
   * "Extra Stiff" in the panel instead. */
  var FLEX_ALIAS = {
    "ladies": ["ladies", "ladies flex", "l flex"],
    "senior": ["lite/senior", "senior/lite", "senior", "lite", "a flex", "a-flex", "amateur"],
    "regular": ["regular", "r flex"],
    "stiff": ["stiff", "s flex"],
    "extra stiff": ["extra stiff", "x-stiff", "x stiff", "extra-stiff", "x flex", "x-flex", "tour x"]
  };

  var GRIP_ALIAS = {
    "undersize": ["undersize"],
    "standard": ["standard"],
    "midsize": ["midsize", "mid size"],
    "jumbo": ["jumbo", "oversize"]
  };

  /* The cookie carries only numbers, so the panel builds its own readable
   * rows from them when the prose record is not available. */
  function lieWord(d) {
    if (d === 0) return "Standard";
    return Math.abs(d) + "\u00b0 " + (d > 0 ? "Upright" : "Flat");
  }

  function specsFromFit(fit, mini) {
    var o = {};
    if (fit.lengthIn != null) {
      var d = fit.lengthDelta;
      o["Length"] = (mini ? MINI_STD + (d || 0) : fit.lengthIn) + '"' +
        (d ? " (" + (d > 0 ? "+" : "\u2212") + Math.abs(d) + '" from standard)' : " (standard)");
    }
    if (fit.lieDeg != null) o["Lie angle"] = lieWord(fit.lieDeg);
    if (fit.flex) o["Shaft flex"] = fit.flex;
    if (fit.loft) o["Driver loft"] = fit.loft;
    if (fit.shaft) o["Shaft"] = fit.shaft;
    var alt = fit.shaft === fit.shaftGraphite ? fit.shaftSteel
      : fit.shaft === fit.shaftSteel ? fit.shaftGraphite : null;
    if (alt) o["Shaft \u2014 alternative"] = alt;
    if (!fit.shaft && fit.shaftGraphite) o["Shaft"] = fit.shaftGraphite;
    if (fit.gripSize) o["Grip size"] = fit.gripSize;
    if (fit.bounce) o["Bounce"] = fit.bounce + " bounce";
    if (fit.gripModel) o["Grip model"] = fit.gripModel;
    if (fit.softInserts) o["Comfort build"] = "Shock-absorbing inserts";
    if (fit.hand) o["Golf stance"] = fit.hand === "left" ? "Left-Handed" : "Right-Handed";
    return o;
  }

  function wantedFor(m, fit, specs) {
    // restore what they chose, not what we suggested
    if (userPicks[m.id]) return { text: [userPicks[m.id]], loose: true, theirs: true };
    switch (m.id) {
      case "length":
        if (fit.lengthIn == null && fit.lengthDelta == null) return null;
        // some products list absolute lengths (45"), others list them
        // relative to standard (Standard, +1/2", -1/2")
        // a driver fitting on a Mini Driver page counts from 43"
        if (isMiniPage() && pageClub() === "driver") {
          return { number: fit.lengthInMini != null ? fit.lengthInMini : MINI_STD + (fit.lengthDelta || 0), delta: fit.lengthDelta };
        }
        return { number: fit.lengthIn, delta: fit.lengthDelta };
      case "loft":   return fit.loft != null ? { number: fit.loft } : null;
      case "lie":
        if (fit.lieDeg == null) return specs["Lie angle"] ? { text: [specs["Lie angle"]] } : null;
        return { text: [lieWord(fit.lieDeg), String(fit.lieDeg) + " deg"] };
      case "flex":
        if (!fit.flex) return null;
        var list = (FLEX_ALIAS[norm(fit.flex)] || [fit.flex]).slice();
        // if this product has no X option, Stiff is the stiffest thing we build
        if (norm(fit.flex) === "extra stiff") list = list.concat(FLEX_ALIAS.stiff);
        return { text: list, strict: true };
      case "grip":   return fit.gripSize ? { text: GRIP_ALIAS[norm(fit.gripSize)] || [fit.gripSize] } : null;
      case "bounce": return fit.bounce ? { text: [fit.bounce] } : null;
      case "head":   return fit.headStyle ? { text: [fit.headStyle] } : null;
      case "hand":   return { text: fit.hand === "left" ? ["left-handed", "left"] : ["right-handed", "right"] };
      case "inserts": return fit.softInserts ? { text: ["yes"] } : null;
      case "gripModel": return fit.gripModel ? { text: [fit.gripModel], loose: true } : null;
      case "shaft":
        /* A pick the customer actually made is never swapped for the other
         * material. Filling graphite because this product does not stock the
         * steel they chose is worse than filling nothing - they would order
         * the opposite of what the sheet told them. Without a pick, the
         * other material stays a reasonable fallback. */
        if (!fit.shaft) return null;
        if (fit.shaftPicked) return { text: [fit.shaft], loose: true, picked: true };
        var names = [fit.shaft, fit.shaftGraphite, fit.shaftSteel].filter(Boolean);
        return { text: names, loose: true };
    }
    return null;
  }

  /* ---------- picking an <option> ---------- */

  /* APO's "-- PLEASE SELECT --" is not always blank - on some products it
   * carries a value such as "0". Checking only for an empty value missed
   * every reset on those products, so nothing was ever restored. Recognise
   * it by its wording as well. */
  var PLACEHOLDER = /please\s*select|choose\s*(an\s*)?option|^\W*select\W*$|^-{2,}/i;

  function isPlaceholderOption(o) {
    return !o || o.value === "" || PLACEHOLDER.test((o.textContent || "").trim());
  }

  function onPlaceholder(sel) {
    var o = sel.selectedOptions ? sel.selectedOptions[0] : sel.options[sel.selectedIndex];
    // sitting on a copy APO has just hidden is a reset in all but name
    return isPlaceholderOption(o) || apoHidden(o);
  }

  function apoHidden(el) {
    return !el || el.hidden || el.disabled ||
      (el.classList && el.classList.contains("mw-hidden")) ||
      !!(el.closest && el.closest(".mw-hidden")) ||
      el.getAttribute("data-hidden") === "1";
  }

  function usable(select) {
    return Array.prototype.filter.call(select.options, function (o) {
      return !isPlaceholderOption(o) && !apoHidden(o);
    });
  }

  /* Does this list name lengths relative to standard, or absolutely? A list
   * containing "Standard" is relative; one full of numbers near 40 is
   * absolute. Getting this wrong left the field on Standard. */
  function isRelativeLengthList(select) {
    var opts = usable(select), sawStandard = false, sawBigNumber = false;
    for (var i = 0; i < opts.length; i++) {
      var t = tight(opts[i].textContent);
      if (t.indexOf("standard") >= 0) sawStandard = true;
      var v = num(opts[i].textContent);
      if (v != null && v >= 30) sawBigNumber = true;
    }
    return sawStandard && !sawBigNumber;
  }

  // "-1/2\"" and "+1/2 inch" and "1/2 short" all mean the same thing
  function deltaOf(text) {
    var t = norm(text);
    if (t.indexOf("standard") >= 0 && !/[0-9]/.test(t)) return 0;
    var sign = 1;
    if (/^-|\bminus\b|\bshort(er)?\b|\bunder\b/.test(t)) sign = -1;
    if (/^\+|\bplus\b|\blong(er)?\b|\bover\b/.test(t)) sign = 1;
    var frac = t.match(/(\d+)\s*\/\s*(\d+)/);
    var val = null;
    if (frac) {
      val = parseInt(frac[1], 10) / parseInt(frac[2], 10);
      var whole = t.match(/(\d+)\s+\d+\s*\//);
      if (whole) val += parseInt(whole[1], 10);
    } else {
      var n = t.match(/\d*\.\d+|\d+/);
      if (n) val = parseFloat(n[0]);
    }
    if (val == null) return null;
    return sign * val;
  }

  function pickLength(select, want) {
    if (isRelativeLengthList(select)) {
      var target = want.delta == null ? 0 : want.delta;
      var best = null, gap = Infinity;
      usable(select).forEach(function (o) {
        var d = deltaOf(o.textContent);
        if (d == null) return;
        var g = Math.abs(d - target);
        if (g < gap) { gap = g; best = o; }
      });
      if (!best || gap > 1.01) return null;
      return { option: best, exact: gap < 0.01 };
    }
    if (want.number == null) return null;
    return pickNearestNumber(select, want.number);
  }

  /* Loft asks for a specific number, but drivers stock only a handful. Take
   * the nearest, and if the target sits beyond either end of the list take
   * the closest end rather than giving up. */
  function pickLoft(select, target) {
    var opts = usable(select).filter(function (o) { return num(o.textContent) != null; });
    if (!opts.length) return null;
    var best = null, gap = Infinity;
    opts.forEach(function (o) {
      var g = Math.abs(num(o.textContent) - target);
      if (g < gap) { gap = g; best = o; }
    });
    return best ? { option: best, exact: gap < 0.01 } : null;
  }

  function pickNearestNumber(select, target) {
    var best = null, bestGap = Infinity;
    usable(select).forEach(function (o) {
      var v = num(o.textContent);
      if (v == null) return;
      var gap = Math.abs(v - target);
      if (gap < bestGap) { bestGap = gap; best = o; }
    });
    // a length or loft option list is in inches or degrees; anything more than
    // 1.5 away is a different kind of list and not worth guessing at
    if (!best || bestGap > 1.5) return null;
    return { option: best, exact: bestGap < 0.01, gap: bestGap };
  }

  function pickText(select, wants, loose, strict) {
    var opts = usable(select), i, k;
    // exact, then bracket-stripped exact, then containment preferring the
    // shortest option. strict stops before containment: for flex a near miss
    // is a wrong club, so no match beats a close one.
    for (k = 0; k < wants.length; k++) {
      var w = tight(wants[k]);
      if (!w) continue;
      for (i = 0; i < opts.length; i++) {
        if (tight(opts[i].textContent) === w) return { option: opts[i], exact: true };
      }
      for (i = 0; i < opts.length; i++) {
        var t = tight(opts[i].textContent).replace(/\(.*?\)/g, "");
        if (t === w) return { option: opts[i], exact: true };
      }
      if (strict) continue;
      var cands = [];
      for (i = 0; i < opts.length; i++) {
        var full = tight(opts[i].textContent);
        if (full.indexOf(w) >= 0) cands.push(opts[i]);
      }
      if (cands.length) {
        cands.sort(function (a, b) { return tight(a.textContent).length - tight(b.textContent).length; });
        return { option: cands[0], exact: true };
      }
      if (loose) {
        for (i = 0; i < opts.length; i++) {
          var ot = tight(opts[i].textContent);
          if (ot.length > 3 && w.indexOf(ot) >= 0) return { option: opts[i], exact: true };
        }
      }
    }
    return null;
  }

  function fire(node) {
    ["input", "change"].forEach(function (t) {
      node.dispatchEvent(new Event(t, { bubbles: true }));
    });
  }

  /* ---------- walking the APO options ---------- */

  function container() {
    return document.querySelector(".mw-apo-container, [class*='mw-apo'], [class*='mw-option']")
      ? (document.querySelector(".mw-apo-container") || document.body)
      : null;
  }

  function titleOf(block) {
    var t = block.querySelector(".mw-label .mw-text--style-strong, .mw-label .mw-text, .mw-label, label");
    return t ? t.textContent : "";
  }

  /* Some options are gated behind a checkbox - "Lie Angle Adjustment" is
   * one - and the select that carries the value does not exist until the box
   * is ticked. The select pass alone therefore skipped the whole option and
   * a 2-degree upright fitting ordered as standard. Tick first, then let the
   * observer re-run the select pass once APO has rendered the field. */
  function labelForInput(input) {
    if (input.id) {
      try {
        var l = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (l) return l.textContent;
      } catch (e) {}
    }
    var wrap = input.closest("label");
    if (wrap) return wrap.textContent;
    var sib = input.nextElementSibling;
    if (sib) return sib.textContent;
    return input.getAttribute("data-value") || input.name || "";
  }

  var LIE_MATCHER = { id: "lie", kind: "text" };
  // boxes this script ticked, so Clear can undo its own work and nothing else
  var tickedBoxes = [];
  /* What each control held before this script ever touched it. Recorded once:
   * a later pass would otherwise capture our own value as the original and
   * make Clear a no-op. */
  var originals = {};

  // only: when given, the matcher ids this pass may touch (a single-item fitting)
  function gateCheckboxes(fit, specs, only) {
    var root = document.querySelector(".mw-apo-container") || document.body;
    var blocks = root.querySelectorAll(".mw-option, .mw-option__container");
    var ticked = 0;

    Array.prototype.forEach.call(blocks, function (block) {
      /* APO groups several checkboxes into one block - "Premium Assembly
       * Options" carries both spine alignment and zero-tolerance. Inspecting
       * only the first would let one unrecognised box hide every other in the
       * block, which is how the lie gate was being missed. */
      var boxes = block.querySelectorAll('input[type="checkbox"]');
      if (!boxes.length) return;
      var blockTitle = titleOf(block);

      Array.prototype.forEach.call(boxes, function (box) {
        if (box.disabled || box.checked) return;
        // APO keeps a hidden copy of each add-on per club count; never tick one
        var vbox = box.closest(".mw-option__value") || box;
        if (apoHidden(vbox) || vbox.offsetParent === null) return;
        var key = gateKey(box);
        // they turned this off deliberately; do not put it back
        if (key && userGates[key] === false) return;
        // they turned it on themselves, so restore it whatever the fitting says
        if (key && userGates[key] === true) {
          filling = true;
          box.checked = true;
          fire(box);
          try { box.dispatchEvent(new MouseEvent("click", { bubbles: true })); } catch (e) {}
          if (!box.checked) { box.checked = true; fire(box); }
          filling = false;
          ticked++;
          log("restored the customer's own tick:", JSON.stringify(key));
          return;
        }
        var own = labelForInput(box);
        // with several boxes sharing a block the block title describes none of
        // them, so it may only classify a box that stands alone
        var m = classify(own) || (boxes.length === 1 ? classify(blockTitle) : null);
        var label = norm(own || blockTitle).slice(0, 40);
        if (!m && fit.lieDeg && /\blie\b/.test(norm(own))) {
          m = LIE_MATCHER;
          log("gate: treating", JSON.stringify(label), "as the lie option");
        }
        if (!m) { log("gate skip:", JSON.stringify(label)); return; }
        if (only && only.indexOf(m.id) < 0) return;

        // only tick when we hold a value for it, so no paid extra is added
        // that the fitting never asked for
        var want = wantedFor(m, fit, specs);
        if (!want) { log("gate has no value for", m.id, JSON.stringify(label)); return; }
        if (m.id === "lie" && !fit.lieDeg) return;
        if (m.id === "length" && !fit.lengthDelta) return;
        if (m.id === "inserts" && !fit.softInserts) return;

        filling = true;
        box.checked = true;
        fire(box);
        try { box.dispatchEvent(new MouseEvent("click", { bubbles: true })); } catch (e) {}
        // a click on an already-checked box would undo it
        if (!box.checked) { box.checked = true; fire(box); }
        filling = false;
        ticked++;
        if (tickedBoxes.indexOf(box) < 0) tickedBoxes.push(box);
        log("ticked gate for", m.id, JSON.stringify(label));
      });
    });
    return ticked;
  }

  function ownTitle(node, stopAt) {
    var n = node;
    while (n && n !== stopAt && n !== document.body) {
      var p = n.previousElementSibling;
      while (p) {
        if (p.matches && p.matches(".mw-label, label, legend, .mw-option-title, .mw-option__title")) {
          if (norm(p.textContent)) return p.textContent;
        }
        var inner = p.querySelector && p.querySelector(".mw-label, legend, .mw-option-title");
        if (inner && norm(inner.textContent)) return inner.textContent;
        p = p.previousElementSibling;
      }
      n = n.parentElement;
    }
    return "";
  }

  function apply(fit, specs, only, blankOnly) {
    var root = document.querySelector(".mw-apo-container") || document.body;
    var blocks = root.querySelectorAll(".mw-option, .mw-option__container");
    if (!blocks.length) blocks = root.querySelectorAll("div");
    var applied = [], seen = {}, unmatched = [];

    Array.prototype.forEach.call(blocks, function (block) {
      /* A block can hold several selects - a checkbox gate renders its own
       * select inside the block that gated it - so each one is read on its
       * own terms rather than taking the block's title. */
      var selects = block.querySelectorAll("select.mw-option__control, select");
      if (!selects.length) return;

      Array.prototype.forEach.call(selects, function (select) {
      if (select.disabled || usable(select).length < 1) return;
      if (seen["sel:" + (select.name || select.id)]) return;

      var own = ownTitle(select, block);
      var title = own || titleOf(block);
      var m = classify(own) || (selects.length === 1 ? classify(titleOf(block)) : null);
      if (!m) { log("skip:", JSON.stringify(norm(title).slice(0, 40))); return; }
      if (only && only.indexOf(m.id) < 0) return;
      if (seen[m.id]) return;

      var want = wantedFor(m, fit, specs);
      if (!want) { log("no saved value for", m.id, "(" + norm(title).slice(0, 30) + ")"); return; }

      // a single-item fitting only refills a field left on --Please Select--
      if (blankOnly && blankOnly.has(select) && !onPlaceholder(select)) { seen[m.id] = 1; return; }

      var hit;
      // their own length is matched by name; pickLength would read it as standard
      if (m.id === "length" && !want.theirs) hit = pickLength(select, want);
      else if (m.id === "loft" && want.number != null) hit = pickLoft(select, want.number);
      else if (m.kind === "nearestNumber" && want.number != null) hit = pickNearestNumber(select, want.number);
      else hit = pickText(select, want.text || [], want.loose, want.strict);

      if (!hit) {
        log("no option matched", m.id, want, "in", usable(select).map(function (o) { return norm(o.textContent); }));
        if (want.picked) unmatched.push({ field: "Shaft", value: fit.shaft });
        return;
      }

      seen[m.id] = 1;
      seen["sel:" + (select.name || select.id)] = 1;
      var key = select.name || select.id || ("sel" + i);
      if (!(key in originals)) originals[key] = select.value;
      var before = originals[key];
      if (select.value !== hit.option.value) {
        filling = true;
        select.value = hit.option.value;
        fire(select);
        filling = false;
      }
      if (blankOnly) blankOnly.add(select);
      applied.push({
        id: m.id,
        label: cleanLabel(title),
        value: cleanValue(hit.option.textContent),
        exact: hit.exact !== false,
        select: select,
        setTo: hit.option.value,
        wasValue: before
      });
      log("set", m.id, "→", norm(hit.option.textContent), hit.exact === false ? "(closest)" : "");
      });
    });

    log("applied:", applied, "| unmatched:", unmatched);
    applied.unmatched = unmatched;
    return applied;
  }

  /* ---------- UI ---------- */

  function el(tag, css, text) {
    var n = document.createElement(tag);
    if (css) n.setAttribute("style", css);
    if (text != null) n.textContent = text;
    return n;
  }
  var BTN = "font: 600 13px/1.1 inherit; padding: 5px 11px; border-radius: 4px; cursor: pointer;";

  /* A label may be given as [wide, narrow]; the pair is rendered as two
   * spans and CSS shows the one that fits, so a rotation needs no JS. */
  function chip(label, css, onClick) {
    var b = el("button", BTN + css, Array.isArray(label) ? null : label);
    if (Array.isArray(label)) {
      b.appendChild(el("span", null, label[0])).className = "ajg-bar__long";
      b.appendChild(el("span", null, label[1])).className = "ajg-bar__short";
    }
    b.type = "button";
    b.addEventListener("click", onClick);
    return b;
  }

  var PANEL_ORDER = [
    "Stance", "Shaft", "Shaft \u2014 alternative", "Shaft \u2014 graphite", "Shaft \u2014 steel",
    "Shaft flex", "Length", "Iron length", "Putter length",
    "Lie angle", "Grip size", "Bounce", "Driver loft", "Swing weight", "Grip model", "Golf stance"
  ];

  function orderedKeys(specs) {
    var keys = Object.keys(specs);
    return keys.slice().sort(function (a, b) {
      var ia = PANEL_ORDER.indexOf(a), ib = PANEL_ORDER.indexOf(b);
      if (ia < 0) ia = PANEL_ORDER.length + keys.indexOf(a);
      if (ib < 0) ib = PANEL_ORDER.length + keys.indexOf(b);
      return ia - ib;
    });
  }

  var LABEL_TO_ID = {
    "Length": "length", "Iron length": "length", "Putter length": "length",
    "Driver loft": "loft", "Lie angle": "lie", "Shaft flex": "flex",
    "Shaft": "shaft", "Shaft \u2014 alternative": "shaft",
    "Shaft \u2014 graphite": "shaft", "Shaft \u2014 steel": "shaft",
    "Grip size": "grip", "Bounce": "bounce", "Golf stance": "hand", "Dexterity": "hand", "Stance": "hand"
  };

  /* "Email me my specs" after the fact. One button keeps the panel quiet; it
   * opens into an address field with a Send button on the same row. */
  function newToken() {
    try {
      var b = new Uint8Array(16); crypto.getRandomValues(b);
      return Array.prototype.map.call(b, function (x) { return ("0" + x.toString(16)).slice(-2); }).join("");
    } catch (e) { return (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 32); }
  }

  function reportFor(saved) {
    if (saved && saved.report && saved.report.length) return saved.report;
    // older saves carry only label -> value pairs; send what we have
    return Object.keys((saved && saved.clubs) || {}).map(function (k) {
      var c = saved.clubs[k], sp = c.specs || specsFromFit(c.fit || {});
      return { name: c.name || k, rows: Object.keys(sp).map(function (l) { return { label: l, value: sp[l] }; }) };
    });
  }

  function emailBlock(club) {
    var narrow = window.innerWidth <= 640;
    var wrap = el("div", "margin-top: 16px;");
    var open = chip(narrow ? "Email my specs" : "Email me my specs",
      " width: 100%; padding: 11px; background: " + ACCENT + "; color: #fff; border: 2px solid " + ACCENT + "; font-size: 15px;",
      function () {
        wrap.replaceChildren();
        var row = el("div", "display: flex; gap: 8px;");
        var input = el("input", "flex: 1 1 0; min-width: 0; padding: 10px 12px; font-size: 16px; font-family: inherit; border: 2px solid " + LINE + "; border-radius: 6px; color: " + INK + ";");
        input.type = "email"; input.placeholder = "Your email address"; input.autocomplete = "email";
        input.setAttribute("aria-label", "Your email address");
        var send = chip("Send", " flex: 0 0 auto; padding: 10px 16px; background: " + ACCENT + "; color: #fff; border: 2px solid " + ACCENT + "; font-size: 15px;", submit);
        var note = el("div", "margin-top: 6px; font-size: 13px; color: " + ACCENT + ";");
        input.addEventListener("keydown", function (e) { if (e.key === "Enter") { e.preventDefault(); submit(); } });
        row.appendChild(input); row.appendChild(send);
        wrap.appendChild(row); wrap.appendChild(note);
        setTimeout(function () { try { input.focus(); } catch (e) {} }, 30);

        function submit() {
          var em = String(input.value || "").trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) { note.textContent = "Please enter a valid email address."; input.focus(); return; }
          send.disabled = true; send.textContent = "Sending\u2026"; note.textContent = "";
          var saved = readSaved() || {};
          var body = JSON.stringify({
            key: LOG_KEY, event: "email", fittingId: saved.fittingId || "", email: em,
            optIn: true, source: "product page", token: newToken(), saved: saved,
            report: reportFor(saved)
          });
          var failed = false;
          try {
            fetch(LOG_URL, { method: "POST", mode: "no-cors", keepalive: true, headers: { "Content-Type": "text/plain" }, body: body })
              .catch(function () { failed = true; send.disabled = false; send.textContent = "Send"; note.textContent = "That didn't go through. Please try again."; });
          } catch (e) {}
          setTimeout(function () {
            if (failed) return;
            wrap.replaceChildren(el("div", "padding: 10px 12px; background: " + CREAM + "; border: 1px solid " + LINE + "; border-radius: 6px; font-size: 14px; color: " + INK + ";",
              "\u2713 Sent to " + em + ". It can take a minute to arrive \u2014 check your spam folder if you don't see it."));
          }, 700);
        }
      });
    wrap.appendChild(open);
    return wrap;
  }

  function showPanel(club, specs, applied) {
    // stance first, under the name the product page uses, whether or not the
    // saved record carried it as a row
    var hand = (club && club.fit && club.fit.hand) || "";
    var shown = {};
    if (hand) shown["Stance"] = hand === "left" ? "Left-Handed" : "Right-Handed";
    var RENAME = { "More offset": "High offset", "Less offset": "Low offset" };
    Object.keys(specs || {}).forEach(function (k) {
      if (k === "Golf stance" || k === "Dexterity") { if (!hand) shown["Stance"] = specs[k]; return; }
      shown[k] = RENAME[specs[k]] || specs[k];
    });
    specs = shown;
    var old = document.querySelector("[data-ajg-panel]");
    if (old) old.remove();
    var back = el("div", "position: fixed; inset: 0; background: rgba(43,33,28,.55); z-index: 2147483646;");
    var panel = el("div", "position: fixed; z-index: 2147483647; left: 50%; top: 50%; transform: translate(-50%,-50%); " +
      "width: min(440px, calc(100vw - 32px)); max-height: 82vh; overflow: auto; background: #fff; border-radius: 10px; " +
      "padding: 24px; box-shadow: 0 24px 60px rgba(0,0,0,.3); color: " + INK + "; font-family: inherit;");
    panel.setAttribute("data-ajg-panel", "1");
    panel.appendChild(el("div", "font: 700 13px/1 inherit; letter-spacing: .14em; text-transform: uppercase; color: " +
      ACCENT + "; margin-bottom: 6px;", "Your fitting recommendations"));
    panel.appendChild(el("h3", "margin: 0 0 16px; font-size: 21px;", club.name));
    var byId = {};
    (applied || []).forEach(function (a) { if (!byId[a.id]) byId[a.id] = a; });

    orderedKeys(specs).forEach(function (k) {
      var hit = byId[LABEL_TO_ID[k]];
      var near = hit && hit.exact === false;
      var wrap = el("div", "padding: 11px 0; border-bottom: 1px solid " + LINE + ";");
      var row = el("div", "display: flex; justify-content: space-between; gap: 16px; align-items: baseline;");
      row.appendChild(el("span", "font-size: 14px; color: #7a6a60;", k));
      row.appendChild(el("span", "font-size: 15px; font-weight: 700; text-align: right;",
        hit ? hit.value : specs[k]));
      wrap.appendChild(row);
      // this product does not offer the exact spec, so say what we picked
      if (near) {
        wrap.appendChild(el("div", "margin-top: 3px; font-size: 13px; line-height: 1.45; color: #7a6a60;",
          "Closest match \u2014 you were fitted for " + specs[k] + ", which this model does not offer."));
      }
      panel.appendChild(wrap);
    });
    panel.appendChild(emailBlock(club));
    panel.appendChild(el("p", "margin: 16px 0 0; font-size: 13px; line-height: 1.55; color: #7a6a60;",
      "Questions about any of these specs? Talk to one of our fitters at 602-702-5317 before you order."));
    function kill() { back.remove(); panel.remove(); }
    panel.appendChild(chip("Close", " margin-top: 16px; width: 100%; padding: 13px; background: " + INK +
      "; color: " + CREAM + "; border: 0; font-size: 15px;", kill));
    back.addEventListener("click", kill);
    document.body.appendChild(back);
    document.body.appendChild(panel);
  }

  /* Flex-wrap alone gave a ragged bar on a phone: the message wrapped to two
   * lines while the buttons broke onto separate rows. Below 600px the bar
   * becomes two deliberate rows - message, then both buttons side by side. */
  function barStyles() {
    if (document.getElementById("ajg-bar-css")) return;
    var t = document.createElement("style");
    t.id = "ajg-bar-css";
    t.textContent =
      "[data-ajg-fit] .ajg-bar__actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}" +
      "[data-ajg-fit] .ajg-bar__short{display:none}" +
      "@media (max-width:600px){" +
      "[data-ajg-fit]{display:block !important}" +
      "[data-ajg-fit] .ajg-bar__msg{display:block;min-width:0 !important;margin-bottom:7px}" +
      "[data-ajg-fit] .ajg-bar__actions{flex-wrap:nowrap}" +
      "[data-ajg-fit] .ajg-bar__actions>button{flex:1 1 0;min-width:0;white-space:nowrap;" +
      "overflow:hidden;text-overflow:ellipsis;padding-left:8px;padding-right:8px}" +
      "[data-ajg-fit] .ajg-bar__long{display:none}" +
      "[data-ajg-fit].ajg-bar--invite .ajg-bar__msg{text-align:center}" +
      "[data-ajg-fit].ajg-bar--invite .ajg-bar__actions{justify-content:center}" +
      "[data-ajg-fit] .ajg-bar__short{display:inline}" +
      "}";
    document.head.appendChild(t);
  }

  function bar(club, applied, specs) {
    barStyles();
    var old = document.querySelector("[data-ajg-fit]");
    if (old) old.remove();
    var box = el("div", "margin: 0 0 10px; padding: 5px 10px; background: " + CREAM + "; border: 1px solid " + LINE +
      "; border-radius: 6px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; color: " + INK +
      "; font-family: inherit; box-sizing: border-box;");
    box.setAttribute("data-ajg-fit", "1");

    var msg = el("span", "flex: 1; min-width: 200px; font-size: 14px; line-height: 1.35;");
    msg.className = "ajg-bar__msg";
    var missed = applied.unmatched || [];
    var stance = missed.filter(function (m) { return m.switchTo; })[0];
    if (stance) {
      msg.appendChild(el("strong", "color: " + ACCENT + ";", stance.msg + " "));
      var links = el("span", "white-space: nowrap; font-size: 14px;");
      var shop = el("a", "color: " + ACCENT + "; text-decoration: underline; font-weight: 700;", stance.linkText);
      shop.href = stance.href;
      links.appendChild(shop);
      links.appendChild(document.createTextNode(" \u00b7 "));
      var sw = el("a", "color: " + ACCENT + "; text-decoration: underline; font-weight: 700; cursor: pointer;", "Switch to " + (stance.switchTo === "right" ? "RH" : "LH"));
      sw.href = "#";
      sw.addEventListener("click", function (e) { e.preventDefault(); switchStance(stance.switchTo); });
      links.appendChild(sw);
      msg.appendChild(links);
    } else if (applied.length) {
      msg.appendChild(el("strong", "color: " + ACCENT + ";", "Your fitting specs are filled in below."));
      if (missed.length) {
        var note = el("span", "display: block; margin-top: 4px; font-size: 14px; color: " + (missed[0].alert ? ACCENT : INK) + ";" + (missed[0].alert ? " font-weight: 700;" : ""),
          missed[0].msg ||
          "We do not offer " + missed[0].value + " on this club \u2014 choose a shaft below, or call 602-702-5317.");
        if (missed[0].href) {
          var go = el("a", "display: inline-block; margin-left: 6px; color: " + ACCENT + "; text-decoration: underline; font-weight: 700;", missed[0].linkText || "Shop");
          go.href = missed[0].href;
          note.appendChild(go);
        }
        msg.appendChild(note);
      }
      // the full list lives in the panel; only flag the one thing the
      // customer could not otherwise know from looking at the dropdowns
      if (applied.some(function (a) { return !a.exact; })) {
        msg.appendChild(document.createTextNode(" One or two are the closest this model offers \u2014 see your specs for details."));
      }
    } else {
      msg.appendChild(el("strong", "color: " + ACCENT + ";", "We have your fitting specs on file. "));
      msg.appendChild(document.createTextNode("Open them and pick the matching options below."));
    }
    box.appendChild(msg);

    var actions = el("div", null);
    actions.className = "ajg-bar__actions";
    box.appendChild(actions);

    actions.appendChild(chip(applied.length
      ? ["See all my specs", "See my specs"]
      : ["View our fitting recommendations", "See my specs"],
      applied.length
        ? " background: transparent; color: " + INK + "; border: 2px solid " + INK + ";"
        : " background: " + ACCENT + "; color: #fff; border: 0;",
      function () { showPanel(club, specs, applied); }));

    actions.appendChild(chip(["Clear my fitting info", "Clear my fitting"],
      " background: transparent; color: #7a6a60; border: 2px solid " + LINE + ";",
      function () {
        // keep a copy in memory only, so Restore works until the page reloads
        var snapshot = { club: club, specs: specs, applied: applied.slice(), fit: lastFit, fitSpecs: lastSpecs };
        clearSaved(clubKeyFor(club));
        stop();
        stopGuard();
        var n = revertApplied(applied);

        box.replaceChildren();
        var note = el("span", "flex: 1; min-width: 200px; font-size: 14px; line-height: 1.35; color: #7a6a60;",
          n ? "Fitting info cleared and your options reset."
            : "Fitting info cleared. Your own selections are unchanged.");
        note.className = "ajg-bar__msg";
        box.appendChild(note);

        var back = el("div", null);
        back.className = "ajg-bar__actions";
        box.appendChild(back);

        back.appendChild(chip(["Restore my fitting specs", "Restore my specs"],
          " background: transparent; color: " + INK + "; border: 2px solid " + INK + ";",
          function () {
            reapply(snapshot.applied, snapshot.fit, snapshot.fitSpecs);
            saveBack(snapshot);
            bar(snapshot.club, snapshot.applied, snapshot.specs);
            lastFilled = snapshot.applied.slice();
            if (snapshot.fit) startGuard(snapshot.club, snapshot.fit, snapshot.fitSpecs);
          }));

        // clearing is often the first move of "these are wrong, fit me again",
        // so offer that here rather than making them hunt for the link
        back.appendChild(chip("Start a new fitting",
          " background: " + ACCENT + "; color: #fff; border: 2px solid " + ACCENT + ";",
          function () { openFittingModal(true); }));
      }));

    placeBarWhenReady(box);
  }

  /* ---------- wait for APO ---------- */

  var observer = null, timer = null, done = false, deadline = 0;

  /* A fitting is a recommendation, not a requirement. Once the customer picks
   * something else we remember it and restore THAT on a re-render, rather
   * than snapping back to what we suggested. Keyed by option id and stored
   * as the visible option text, so it survives APO rebuilding the select
   * with new values and prices for a different set configuration. */
  var userPicks = {};
  var filling = false;

  /* Checkbox state the customer set, keyed by the box's own label. Keyed by
   * label rather than element because APO replaces the nodes outright when a
   * set configuration changes, and by label rather than by matcher id so the
   * options we never tick ourselves - spine alignment, pro-soft inserts -
   * survive too. */
  var userGates = {};

  function gateKey(box) {
    return norm(labelForInput(box)).replace(/\+?\$[0-9.,]+/g, "").trim();
  }

  function recordUserPick(e) {
    if (filling) return;                       // that change was ours
    refills = 0; pickRefills = 0;
    var sel = e.target;

    if (sel && sel.tagName === "INPUT" && sel.type === "checkbox") {
      var block = sel.closest(".mw-option, .mw-option__container");
      if (!block) return;
      var k = gateKey(sel);
      if (!k) return;
      userGates[k] = sel.checked;
      log("customer " + (sel.checked ? "ticked" : "unticked") + ":", JSON.stringify(k));
      return;
    }

    if (!sel || sel.tagName !== "SELECT") return;
    var block = sel.closest(".mw-option, .mw-option__container");
    if (!block) return;
    var own = ownTitle(sel, block);
    var selects = block.querySelectorAll("select.mw-option__control, select");
    var m = classify(own) || (selects.length === 1 ? classify(titleOf(block)) : null);
    if (!m) return;
    var opt = sel.selectedOptions && sel.selectedOptions[0];
    var text = opt ? norm(opt.textContent) : "";
    if (!text || onPlaceholder(sel)) {
      // a reset to the placeholder is APO's doing, not a choice
      return;
    }
    /* A field a single-item fitting covers is not remembered: if APO resets
     * it to --Please Select--, the recommendation goes back in, not this. */
    if (quickCovers(m.id)) { delete userPicks[m.id]; log("customer changed a fitted", m.id, "by hand"); return; }
    // strip the price suffix so the same shaft matches at a different set price
    var bare = text.replace(/\+?\$[0-9.,]+/g, "").replace(/\(\s*\)/g, "").trim();
    userPicks[m.id] = bare || text;
    log("customer chose their own", m.id + ":", JSON.stringify(userPicks[m.id]));
  }

  var guard = null, guardTimer = null, refills = 0, lastFilled = [];
  var lastFit = null, lastSpecs = null;

  function needsRefill() {
    for (var i = 0; i < lastFilled.length; i++) {
      var sel = lastFilled[i].select;
      if (!sel) continue;
      // the whole option block was replaced
      if (!document.contains(sel)) return "re-rendered";
      // still there but back on its placeholder
      // a value the customer chose is never the placeholder, so only that counts
      if (onPlaceholder(sel)) return "reset to placeholder";
    }

    var lost = "";
    if (false) Object.keys(userGates).forEach(function (k) {
      if (lost || userGates[k] !== true) return;
      var root = document.querySelector(".mw-apo-container") || document.body;
      var boxes = root.querySelectorAll('input[type="checkbox"]');
      for (var j = 0; j < boxes.length; j++) {
        if (gateKey(boxes[j]) !== k) continue;
        if (!boxes[j].checked) lost = "cleared a ticked option";
        return;
      }
    });
    if (lost) return lost;

    return "";
  }

  function stopGuard() {
    if (guard) { guard.disconnect(); guard = null; }
    clearTimeout(guardTimer);
  }

  // clearing a fitting should not cost them the choices they made by hand
  function guardOwnChoicesOnly() {
    stopGuard();
    pickRefills = 0;
    startPickGuard();
  }

  /* ---------- remembering choices with no fitting at all ----------
   * APO resets its dependent options whenever the set configuration changes,
   * which loses work whether or not the customer was ever fitted. This is the
   * same memory as above with nothing from the fitting involved: only what
   * they chose themselves is restored, by name, and never anything the new
   * configuration stopped offering. No bar, no recommendations. */
  function restoreOwnChoices() {
    var root = document.querySelector(".mw-apo-container") || document.body;
    var restored = 0;

    Array.prototype.forEach.call(root.querySelectorAll('input[type="checkbox"]'), function (box) {
      if (box.disabled) return;
      var k = gateKey(box);
      if (!k || userGates[k] !== true || box.checked) return;
      filling = true;
      box.checked = true;
      fire(box);
      try { box.dispatchEvent(new MouseEvent("click", { bubbles: true })); } catch (e) {}
      if (!box.checked) { box.checked = true; fire(box); }
      filling = false;
      restored++;
      log("restored their tick:", JSON.stringify(k));
    });

    Array.prototype.forEach.call(root.querySelectorAll(".mw-option, .mw-option__container"), function (block) {
      var selects = block.querySelectorAll("select.mw-option__control, select");
      Array.prototype.forEach.call(selects, function (sel) {
        if (sel.disabled || !onPlaceholder(sel)) return;   // only a reset counts
        var own = ownTitle(sel, block);
        var m = classify(own) || (selects.length === 1 ? classify(titleOf(block)) : null);
        if (!m || !userPicks[m.id]) return;
        var hit = pickText(sel, [userPicks[m.id]], true);
        // this configuration may simply not offer it any more - leave it be
        if (!hit) { log("their " + m.id + " is not offered here; leaving it unset"); return; }
        filling = true;
        sel.value = hit.option.value;
        fire(sel);
        filling = false;
        restored++;
        log("restored their " + m.id + ":", cleanValue(hit.option.textContent));
      });
    });

    return restored;
  }

  function ownChoicesLost() {
    if (!Object.keys(userPicks).length && !Object.keys(userGates).length) return false;
    var root = document.querySelector(".mw-apo-container") || document.body;

    var boxes = root.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < boxes.length; i++) {
      var k = gateKey(boxes[i]);
      if (k && userGates[k] === true && !boxes[i].checked) return true;
    }

    var blocks = root.querySelectorAll(".mw-option, .mw-option__container");
    for (var b = 0; b < blocks.length; b++) {
      var selects = blocks[b].querySelectorAll("select.mw-option__control, select");
      for (var j = 0; j < selects.length; j++) {
        if (selects[j].disabled || !onPlaceholder(selects[j])) continue;
        var own = ownTitle(selects[j], blocks[b]);
        var m = classify(own) || (selects.length === 1 ? classify(titleOf(blocks[b])) : null);
        if (m && userPicks[m.id]) return true;
      }
    }
    return false;
  }

  var pickGuard = null, pickTimer = null, pickRefills = 0;

  function startPickGuard() {
    // owned by the "keep selections" snippet in aj-custom.js
    return;
    if (pickGuard) return;
    if (!document.querySelector(".mw-apo-container, .mw-option")) {
      // APO renders late; try again once it has
      setTimeout(startPickGuard, 1500);
      return;
    }
    pickGuard = new MutationObserver(function () {
      clearTimeout(pickTimer);
      pickTimer = setTimeout(function () {
        if (pickRefills > 40 || !ownChoicesLost()) return;
        pickRefills++;
        log("options were reset; restoring the customer's own choices");
        if (restoreOwnChoices()) {
          // a restored gate can render another option a moment later
          setTimeout(restoreOwnChoices, 400);
        }
      }, SETTLE_MS + 150);
    });
    pickGuard.observe(document.body, { childList: true, subtree: true });
  }

  function startGuard(club, fit, specs) {
    if (guard) return;
    guard = new MutationObserver(function () {
      clearTimeout(guardTimer);
      guardTimer = setTimeout(function () {
        if (refills > 30) return;
        var why = needsRefill();
        if (!why) return;
        refills++;
        log("options were " + why + "; re-applying the fitting");
        var ticked = gateCheckboxes(fit, specs);
        var again = apply(fit, specs);
        lastFilled = again.slice();
        if (again.length) bar(club, again, specs);
        if (ticked) setTimeout(function () {
          var more = apply(fit, specs);
          if (more.length) { lastFilled = more.slice(); bar(club, more, specs); }
        }, 400);
      }, SETTLE_MS + 150);
    });
    guard.observe(document.body, { childList: true, subtree: true });
  }

  function stop() {
    if (observer) { observer.disconnect(); observer = null; }
    clearTimeout(timer);
    done = true;
  }

  function attempt(club, fit, specs, last) {
    if (done) return;
    var ready = document.querySelector(".mw-apo-container select, .mw-option select");
    if (!ready && !last) return;
    // ticking a gate makes APO render a new select, so hold the watcher open
    // for one more pass rather than stopping on what we filled this time
    lastFit = fit; lastSpecs = specs;
    var ticked = gateCheckboxes(fit, specs);
    var applied = apply(fit, specs);
    var hands = stanceHands();
    if (fit.hand && hands && hands.indexOf(fit.hand) < 0) {
      var other = fit.hand === "left" ? "left-handed" : "right-handed";
      var only = fit.hand === "left" ? "right-handed" : "left-handed";
      var ck = clubKeyFor(club);
      var noun = CLUB_NOUN[ck] || "club";
      (applied.unmatched = applied.unmatched || []).unshift({
        field: "Golf stance",
        msg: stanceSubject(ck) + " only available in " + only + ", but your fitting is for " + other + ".",
        href: "/collections/" + ({ driver: "drivers", wood: "woods", hybrid: "hybrids", irons: "irons", wedge: "wedges", putter: "putters" }[ck] || "") +
          "?filter.v.t.shopify.hand-side=" + encodeURIComponent(fit.hand === "left" ? "gid://shopify/TaxonomyValue/6889" : "gid://shopify/TaxonomyValue/6890"),
        linkText: "Shop " + (fit.hand === "left" ? "LH" : "RH") + " clubs",
        switchTo: fit.hand === "left" ? "right" : "left",
        alert: true
      });
    }

    if (applied.length || last) {
      bar(club, applied, specs);
      if (applied.length) {
        lastFilled = applied.slice();
        startGuard(club, fit, specs);
      }
      if (applied.length && !ticked) stop();
    }
  }

  /* The fitting guide in a modal, so a customer never leaves the product
   * page. The guide posts its specs out to us when they save; we write the
   * cookie and re-run, which fills the dropdowns behind the modal. */
  /* The guide is fetched on first open, not shipped with the page. Product
   * pages therefore carry none of its markup - no weight, and no duplicate
   * content across the store. Shopify blocks iframing its own pages, hence
   * fetch-and-inject rather than an iframe. */
  var modalEl = null, guideLoaded = false, guideLoading = false;

  /* On a phone the floating card wasted a border on every side and relied on
   * vh, which counts the area behind the browser's own chrome - so the last
   * field and the Continue button sat under it. Full-bleed with dvh gives
   * the fitting the whole screen and an accurate one. */
  function modalStyles() {
    if (document.getElementById("ajg-modal-css")) return;
    var t = document.createElement("style");
    t.id = "ajg-modal-css";
    t.textContent =
      "[data-ajg-modal] .ajg-modal__shell{" +
      "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);" +
      "width:min(1140px,calc(100vw - 24px));height:min(92vh,calc(100vh - 24px));" +
      "background:" + CREAM + ";border-radius:10px;overflow:hidden;" +
      "box-shadow:0 30px 80px rgba(0,0,0,.4);display:flex;flex-direction:column}" +
      "@supports (height:100dvh){[data-ajg-modal] .ajg-modal__shell{" +
      "height:min(92dvh,calc(100dvh - 24px))}}" +
      "@media (max-width:700px){[data-ajg-modal] .ajg-modal__shell{" +
      "left:0;top:0;transform:none;width:100vw;height:100vh;border-radius:0;box-shadow:none}" +
      "@supports (height:100dvh){[data-ajg-modal] .ajg-modal__shell{height:100dvh}}" +
      "[data-ajg-modal] .ajg-modal__bar{padding:8px 12px !important}" +
      "}";
    document.head.appendChild(t);
  }

  function buildModal() {
    if (modalEl) return modalEl;
    modalStyles();
    var wrap = el("div", "position: fixed; inset: 0; z-index: 2147483646; display: none;");
    wrap.setAttribute("data-ajg-modal", "1");

    var back = el("div", "position: absolute; inset: 0; background: rgba(43,33,28,.6);");
    wrap.appendChild(back);

    var shell = el("div", null);
    shell.className = "ajg-modal__shell";

    var barEl = el("div", "position: relative; flex: none; display: flex; align-items: center; " +
      "justify-content: center; padding: 8px 56px; background: " + INK + "; color: " + CREAM + "; font-family: inherit;");
    barEl.className = "ajg-modal__bar";
    var mark = document.createElement("img");
    mark.src = LOGO_URL;
    mark.alt = "";
    mark.setAttribute("aria-hidden", "true");
    mark.setAttribute("style", "height: 36px; width: auto; max-width: 100%; object-fit: contain; display: block;");
    // a broken image would leave a gap where the logo should be
    mark.onerror = function () { mark.remove(); };
    barEl.appendChild(mark);

    var close = el("button", "position: absolute; top: 50%; right: 8px; transform: translateY(-50%); " +
      "display: inline-flex; align-items: center; justify-content: center; " +
      "width: 40px; height: 40px; padding: 0; border-radius: 8px; " +
      "font-family: inherit; font-size: 30px; font-weight: 400; line-height: 1; " +
      "background: rgba(255,255,255,.1); " +
      "color: " + CREAM + "; border: 2px solid rgba(255,255,255,.45); cursor: pointer;", "\u00d7");
    close.type = "button";
    close.setAttribute("aria-label", "Close the fitting guide");
    barEl.appendChild(close);
    shell.appendChild(barEl);

    var body = el("div", "flex: 1; overflow: auto; -webkit-overflow-scrolling: touch;");
    body.setAttribute("data-ajg-modal-body", "1");
    body.appendChild(el("div", "padding: 60px 20px; text-align: center; font-size: 15px; color: #7a6a60;",
      "Loading your fitting\u2026"));
    shell.appendChild(body);

    wrap.appendChild(shell);
    document.body.appendChild(wrap);

    close.addEventListener("click", function () { closeFittingModal(); });
    back.addEventListener("click", function () { closeFittingModal(); });
    modalEl = wrap;
    return wrap;
  }

  /* Shop logo for the pop-up header. Hosted on the Shopify CDN rather than
   * inlined, so replacing the file in admin updates it here with no code
   * change. */
  var LOGO_URL = "https://cdn.shopify.com/s/files/1/0662/5414/3673/files/header-logo_1_2.png?v=1784182933";

  var prevOverflow = "";

  /* The theme pins a 55px toolbar to the bottom of every mobile screen and the
   * chat launcher sits just above it. Both overlap a focused form. Hide them
   * for as long as the fitting is open, and put them straight back after. */
  function hideThemeChrome(on) {
    var id = "ajg-hide-chrome";
    var tag = document.getElementById(id);
    if (!on) { if (tag) tag.remove(); document.body.classList.remove("ajg-fitting-open"); return; }
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      tag.textContent =
        "body.ajg-fitting-open .hdt-toolbar," +
        "body.ajg-fitting-open shopify-chat," +
        "body.ajg-fitting-open #shopify-chat," +
        "body.ajg-fitting-open .jdgm-widget.jdgm-floating-tab" +
        "{ display: none !important; }";
      document.head.appendChild(tag);
    }
    document.body.classList.add("ajg-fitting-open");
  }

  var historyPushed = false;

  function onModalPop() {
    // back while the fitting's enlarged photo is open only closes the photo
    if (window.__ajgPhotoOpen) return;
    // the entry we added has been popped; treat it as a close
    historyPushed = false;
    if (modalEl && modalEl.style.display !== "none") closeFittingModal(true);
  }

  function closeFittingModal(fromHistory) {
    if (!modalEl) return;
    // dismissing by button should also consume our history entry
    if (!fromHistory && historyPushed) {
      historyPushed = false;
      window.removeEventListener("popstate", onModalPop, false);
      try { history.back(); } catch (e) {}
    } else if (fromHistory) {
      window.removeEventListener("popstate", onModalPop, false);
    }
    modalEl.style.display = "none";
    document.body.style.overflow = prevOverflow;
    hideThemeChrome(false);
    document.removeEventListener("keydown", onModalKey);
  }

  function onModalKey(e) { if (e.key === "Escape") closeFittingModal(); }

  /* innerHTML never runs <script> tags, so every script in the fetched
   * markup has to be re-created by hand, in order, waiting for external
   * ones (support.js) before the inline logic that depends on them. */
  function runScripts(container, done) {
    var scripts = Array.prototype.slice.call(container.querySelectorAll("script"));
    var i = 0;
    (function next() {
      if (i >= scripts.length) { done && done(); return; }
      var old = scripts[i++];
      var s = document.createElement("script");
      for (var a = 0; a < old.attributes.length; a++) {
        s.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      if (old.src) {
        s.onload = next;
        s.onerror = function () { log("failed to load", old.src); next(); };
        old.parentNode.replaceChild(s, old);
      } else {
        s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
        next();
      }
    })();
  }

  /* APO writes the upcharge into each option's own text, e.g.
   * "SK Fiber Hypersonic (+$3.00)". Read those so the fitting can show what
   * each shaft costs on THIS product - the guide has no price data of its
   * own, and the figure differs per product. */
  function stanceHands() {
    var root = optionsRoot() || document;
    var out = [], found = false;
    Array.prototype.forEach.call(root.querySelectorAll(".mw-option, .mw-option__container"), function (block) {
      var sel = block.querySelector("select");
      if (!sel || found) return;
      var m = classify(titleOf(block));
      if (!m || m.id !== "hand") return;
      found = true;
      Array.prototype.forEach.call(sel.options, function (o) {
        var t = norm(o.textContent);
        if (/\bleft/.test(t) && out.indexOf("left") < 0) out.push("left");
        if (/\bright/.test(t) && out.indexOf("right") < 0) out.push("right");
      });
    });
    // no Stance dropdown, or one we cannot read: assume both, never block
    return found && out.length ? out : null;
  }

  function shaftPrices() {
    var map = {};
    var root = optionsRoot() || document;
    var blocks = root.querySelectorAll(".mw-option, .mw-option__container, div");
    Array.prototype.forEach.call(blocks, function (block) {
      var sel = block.querySelector("select");
      if (!sel) return;
      var m = classify(titleOf(block));
      if (!m || m.id !== "shaft") return;
      Array.prototype.forEach.call(sel.options, function (o) {
        var price = (o.textContent.match(/\(\s*([+-]?\s*\$[0-9.,]+)\s*\)/) || [])[1];
        var name = cleanValue(o.textContent);
        if (name) map[tight(name)] = price ? price.replace(/\s+/g, "") : "included";
      });
    });
    log("shaft prices on this product:", map);
    return map;
  }

  function loadGuide(body, clubKey, quick) {
    if (guideLoaded || guideLoading) return;
    guideLoading = true;
    // the guide reads this and skips the "what are you shopping for" step
    window.__ajgEmbed = {
      embed: true,
      // "flex", "length", "grip" or "loft" opens a single-item fitting; "" the full one
      quick: quick || "",
      club: clubKey || pageClub() || "",
      mini: isMiniPage(),
      prices: shaftPrices(),
      hands: stanceHands(),
      // "These irons are", "This chipper is" - so the pop-up names the product too
      subject: stanceSubject(clubKey || pageClub() || "")
    };
    fetch(FITTING_EMBED, { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var frag = doc.getElementById("ajg-fitting-fragment") || doc.body;
        body.replaceChildren();
        while (frag.firstChild) body.appendChild(frag.firstChild);
        runScripts(body, function () {
          guideLoaded = true;
          guideLoading = false;
          log("fitting guide loaded into the pop-up");
        });
      })
      .catch(function (err) {
        guideLoading = false;
        log("could not load the guide:", err.message);
        body.replaceChildren();
        var p = el("div", "padding: 50px 24px; text-align: center; font-family: inherit; color: " + INK + ";");
        p.appendChild(el("p", "margin: 0 0 14px; font-size: 16px;",
          "We could not load the fitting here."));
        var a = document.createElement("a");
        a.href = FITTING_PAGE;
        a.setAttribute("style", BTN + " background: " + ACCENT + "; color: #fff; border: 0; text-decoration: none; display: inline-block;");
        a.textContent = "Open the full fitting guide";
        p.appendChild(a);
        body.appendChild(p);
      });
  }

  function openFittingModal(fresh, quick) {
    var wrap = buildModal();
    var body = wrap.querySelector("[data-ajg-modal-body]");
    var q = quick || "";
    wrap.style.display = "block";
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    hideThemeChrome(true);
    document.addEventListener("keydown", onModalKey);
    body.scrollTop = 0;
    if (window.__ajgEmbed) window.__ajgEmbed.quick = q;
    loadGuide(body, pageClub(), q);
    /* A guide that is already loaded switches mode on this event; one still
     * loading reads __ajgEmbed.quick when it starts. */
    var sendMode = function () {
      try { window.dispatchEvent(new CustomEvent("ajg:fitting-mode", { detail: { quick: q } })); } catch (e) {}
    };
    sendMode();
    setTimeout(sendMode, 400);
    /* The guide keeps its state between openings, so a fresh start has to ask
     * it to clear. It resets its own answers only - anything saved for other
     * clubs is untouched. */
    if (fresh) {
      var askReset = function () {
        try { window.dispatchEvent(new CustomEvent("ajg:fitting-restart")); } catch (e) {}
      };
      askReset();
      setTimeout(askReset, 400);
      setTimeout(askReset, 1200);
    }
    window.__ajgCloseModal = closeFittingModal;
    /* On a phone the sheet fills the screen, so Back reads as "close this".
     * A history entry makes it behave that way instead of leaving the
     * product page and losing the fitting. */
    try {
      if (!historyPushed) {
        history.pushState({ ajgFitting: 1 }, "", location.href);
        historyPushed = true;
        window.addEventListener("popstate", onModalPop, false);
      }
    } catch (e) {}
  }

  function onSavedFromGuide(payload, full, silent) {
    if (typeof payload !== "string" || !payload) return;
    try {
      document.cookie = KEY + "=" + encodeURIComponent(payload) +
        "; path=/; Max-Age=" + (60 * 60 * 24 * 180) + "; SameSite=Lax";
    } catch (err) {}
    try {
      window.localStorage.setItem(KEY, typeof full === "string" ? full : payload);
    } catch (err) {}
    log("specs received from the fitting pop-up" + (silent ? " (auto)" : ""));

    var fill = function () {
      stop();
      stopGuard();
      done = false;
      observer = null;
      refills = 0;
      run();
    };

    /* An automatic save fills the page behind the pop-up and leaves it open,
     * so closing by any route - button, X, or Back - still lands on a filled
     * order form. An explicit Apply also closes, since that is what pressing
     * it is for. */
    if (silent) { fill(); return; }
    setTimeout(function () {
      if (window.__ajgCloseModal) window.__ajgCloseModal();
      fill();
    }, 900);
  }

  window.addEventListener("ajg:fitting-close", function () {
    closeFittingModal();
  }, false);

  // the guide fires this when it lives in this page (the usual case)
  window.addEventListener("ajg:fitting-saved", function (e) {
    var d = (e && e.detail) || {};
    onSavedFromGuide(d.payload, d.full, !!d.silent);
  }, false);

  // and this when it is served in a frame
  window.addEventListener("message", function (e) {
    var d = e.data;
    if (!d || typeof d !== "object" || d.type !== "ajg_fitting_save") return;
    onSavedFromGuide(d.payload, d.full, !!d.silent);
  }, false);

  /* Put back what each field held before we filled it - but only where the
   * customer has not since chosen something themselves. Their own choice
   * always outranks ours. */
  function revertApplied(applied) {
    var reverted = 0;
    (applied || []).forEach(function (a) {
      if (!a.select || !a.select.isConnected) return;
      if (a.select.value !== a.setTo) return; // customer changed it; leave alone
      a.select.value = a.wasValue;
      fire(a.select);
      reverted++;
    });
    tickedBoxes.forEach(function (box) {
      if (!box.isConnected || !box.checked) return;
      box.checked = false;
      fire(box);
      try { box.dispatchEvent(new MouseEvent("click", { bubbles: true })); } catch (e) {}
      if (box.checked) { box.checked = false; fire(box); }
      reverted++;
    });
    tickedBoxes = [];
    log("reverted", reverted, "of", (applied || []).length, "fields plus its own gates");
    return reverted;
  }

  function reapply(applied, fit, specs) {
    var n = 0;
    if (fit) n += gateCheckboxes(fit, specs);
    (applied || []).forEach(function (a) {
      if (!a.select || !a.select.isConnected) return;
      a.select.value = a.setTo;
      fire(a.select);
      n++;
    });
    return n;
  }

  /* Restore has to rewrite the saved record too, or the specs would vanish
   * again on the next page. We only hold this club's slice in memory, so
   * merge it into whatever else is still saved rather than overwriting. */
  function saveBack(snapshot) {
    var existing = readSaved() || { v: 1, clubs: {} };
    if (!existing.clubs) existing.clubs = {};
    existing.clubs[snapshot.club.key || pageClub() || "club"] = snapshot.club;
    existing.saved = new Date().toISOString();
    var json = JSON.stringify(existing);
    try {
      var enc = encodeURIComponent(json);
      if (enc.length < 3800) {
        document.cookie = KEY + "=" + enc + "; path=/; Max-Age=" + (60 * 60 * 24 * 180) + "; SameSite=Lax";
      }
    } catch (e) {}
    try { window.localStorage.setItem(KEY, json); } catch (e) {}
    log("fitting restored and re-saved");
  }

  /* Finding the options block by class name is brittle - the app can rename
   * its wrapper. Locate a real APO field instead and walk up to the outermost
   * element that still only contains options, then sit above that. */
  function optionsRoot() {
    var direct = document.querySelector(".mw-apo-container, #mw-product-options, .mw-product-options");
    if (direct) return direct;

    var field = document.querySelector(
      'select[name^="mw_opts"], [class*="mw-option"] select, select.mw-option__control'
    );
    if (!field) return null;

    var node = field, best = null, hops = 0;
    while (node && node.parentElement && hops < 10) {
      node = node.parentElement;
      hops++;
      if (node.tagName === "FORM" || node.tagName === "BODY") break;
      var cls = (node.className || "") + "";
      if (/mw[-_]/i.test(cls)) best = node;
    }
    return best;
  }

  function placeBar(box) {
    var root = optionsRoot();
    if (root && root.parentElement) {
      root.parentElement.insertBefore(box, root);
      return true;
    }
    return false;
  }

  function placeBarWhenReady(box) {
    if (placeBar(box)) return;
    // APO renders late; hold the bar until it exists rather than dropping
    // it at the top of the page
    var give_up = Date.now() + WATCH_MS;
    var mo = new MutationObserver(function () {
      if (placeBar(box)) { mo.disconnect(); return; }
      if (Date.now() > give_up) {
        mo.disconnect();
        var host = document.querySelector('form[action*="/cart/add"], .product-form, main') || document.body;
        if (host.firstChild) host.insertBefore(box, host.firstChild); else host.appendChild(box);
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function inviteBar(clubKey) {
    var noun = CLUB_NOUN[clubKey] || "club";
    if (clubKey === "putter" && isChipperPage()) noun = "chipper";
    var old = document.querySelector("[data-ajg-fit]");
    if (old) old.remove();
    var box = el("div", "margin: 0 0 10px; padding: 5px 10px; background: " + CREAM + "; border: 1px solid " + LINE +
      "; border-radius: 6px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; color: " + INK +
      "; font-family: inherit; box-sizing: border-box;");
    box.setAttribute("data-ajg-fit", "1");
    barStyles();
    // the invite is one short line and one button, so it sits centred
    box.style.justifyContent = "center";
    box.style.textAlign = "center";
    box.classList.add("ajg-bar--invite");
    var iMsg = el("span", "flex: 0 1 auto; font-size: 14px; line-height: 1.35;",
      "Not sure which options to choose?");
    iMsg.className = "ajg-bar__msg";
    box.appendChild(iMsg);
    var iActions = el("div", "justify-content: center;");
    iActions.className = "ajg-bar__actions";
    box.appendChild(iActions);

    var link = document.createElement("a");
    // a real href, so middle-click and "open in new tab" still work
    link.href = FITTING_PAGE;
    link.setAttribute("style", BTN + " background: " + ACCENT + "; color: #fff; border: 0; text-decoration: none; display: inline-block;");
    link.textContent = "\u2192 Click here for a free fitting \u2190";
    link.addEventListener("click", function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openFittingModal();
    });
    iActions.appendChild(link);

    placeBarWhenReady(box);
  }

  function run() {
    if (!/\/products\//.test(location.pathname)) { log("not a product page; cart tracking only"); return; }
    var data = readSaved();
    var page = pageClub();
    // nothing saved, or nothing saved for THIS kind of club: offer a fitting
    if (!data || !data.clubs || (page && !data.clubs[page])) {
      if (page) { inviteBar(page); applyQuick(page); }
      else log("no saved specs and page type unknown");
      startPickGuard();
      return;
    }
    var key = detectClub(data);
    if (!key || !data.clubs[key]) return;

    var club = data.clubs[key];
    // a full fitting replaces any single-item results for this club
    dropQuick(key);
    var fit = club.fit || {};
    /* The fitting landed outside what we stock (extra stiff). Filling in a
     * near-miss would undo the "call us" the guide just gave them. */
    if (fit.contactUs) {
      log("fitting needs a phone call; not filling anything in");
      inviteBar(key);
      startPickGuard();
      return;
    }
    var specs = {};
    Object.keys(club.specs || {}).forEach(function (k) {
      if (club.specs[k] && club.specs[k] !== "\u2014") specs[k] = club.specs[k];
    });
    // the cookie carries only the numbers, so build readable rows from them
    if (!Object.keys(specs).length) specs = specsFromFit(fit, key === "driver" && isMiniPage());
    if (!Object.keys(specs).length && !Object.keys(fit).length) {
      log("saved record has nothing usable", club);
      return;
    }
    log("specs:", specs, "fit:", fit);

    deadline = Date.now() + WATCH_MS;
    attempt(club, fit, specs, false);

    observer = new MutationObserver(function () {
      if (done) return;
      clearTimeout(timer);
      timer = setTimeout(function () { attempt(club, fit, specs, Date.now() > deadline); }, SETTLE_MS);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(function () {
      if (done) return;
      log("watch window closed");
      attempt(club, fit, specs, true);
      stop();
    }, WATCH_MS);
  }

  /* ---------- collection pages ----------
   * A customer who has been fitted should not have to re-pick their level
   * every time they browse. The saved score filters the collection for them,
   * once per session and only when they have not filtered it themselves, so
   * it never fights a deliberate choice. */
  var SKILL_FILTER = "filter.p.m.custom.handicap_range";
  var SKILL_ALL = "All Levels";
  var SKILL_VALUE = {
    low: "Low-Handicap (Advanced)",
    mid: "Mid-Handicap (Intermediate)",
    high: "High-Handicap (Beginner)",
    "new": "High-Handicap (Beginner)"
  };
  var OPT_OUT = "ajg_no_level_filter";

  function fitStamp() {
    var d = readSaved();
    return d && d.saved ? String(d.saved) : "1";
  }

  function optedOut() {
    try { return localStorage.getItem(OPT_OUT) === fitStamp(); } catch (e) { return false; }
  }

  var LEVEL_OFF = "ajg_level_off";
  function levelCleared() {
    try { return localStorage.getItem(LEVEL_OFF) === fitStamp(); } catch (e) { return false; }
  }

  function setOptOut(on) {
    try {
      if (on) localStorage.setItem(OPT_OUT, fitStamp());
      else localStorage.removeItem(OPT_OUT);
      sessionStorage.removeItem(OPT_OUT);
    } catch (e) {}
  }

  function levelBar(label, offer) {
    barStyles();
    var old = document.querySelector("[data-ajg-fit]");
    if (old) old.remove();
    var box = el("div", "margin: 0 0 12px; padding: 6px 10px; background: " + CREAM + "; border: 1px solid " + LINE +
      "; border-radius: 6px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; color: " + INK +
      "; font-family: inherit; box-sizing: border-box;");
    box.setAttribute("data-ajg-fit", "1");
    var msg = el("span", "flex: 1; min-width: 180px; font-size: 14px; line-height: 1.35;");
    msg.className = "ajg-bar__msg";
    if (offer) {
      msg.appendChild(el("strong", "color: " + ACCENT + ";", "Filter by your skill level? "));
      msg.appendChild(document.createTextNode("Your fitting says " + label.replace(/\s*\(.*\)\s*$/, "")));
    } else {
      msg.appendChild(el("strong", "color: " + ACCENT + ";", "Filtered to your fitting: "));
      msg.appendChild(document.createTextNode(label));
    }
    box.appendChild(msg);

    var acts = el("div", null);
    acts.className = "ajg-bar__actions";
    box.appendChild(acts);
    if (offer) {
      acts.appendChild(chip(["Filter my clubs", "Filter"],
        " background: " + ACCENT + "; color: #ffffff; border: 2px solid " + ACCENT + ";",
        function () {
          setOptOut(false);
          var u = new URL(location.href);
          u.searchParams.delete(SKILL_FILTER);
          u.searchParams.append(SKILL_FILTER, label);
          u.searchParams.append(SKILL_FILTER, SKILL_ALL);
          u.searchParams.set("ajgfit", "1");
          location.replace(u.toString());
        }));
    } else {
      acts.appendChild(chip(["Show every club", "Show all"],
        " background: transparent; color: #7a6a60; border: 2px solid " + LINE + ";",
        function () {
          // remembered on every page until they do a new fitting
          setOptOut(true);
          var u = new URL(location.href);
          u.searchParams.delete(SKILL_FILTER);
          u.searchParams.delete("ajgfit");
          location.replace(u.toString());
        }));
    }
    {
      acts.appendChild(chip("Clear fitting",
        " background: transparent; color: #7a6a60; border: 2px solid " + LINE + ";",
        function () {
          try { localStorage.setItem(LEVEL_OFF, fitStamp()); } catch (e) {}
          setOptOut(false);
          var u = new URL(location.href);
          if (u.searchParams.get("ajgfit")) {
            u.searchParams.delete(SKILL_FILTER);
            u.searchParams.delete("ajgfit");
            location.replace(u.toString());
          } else {
            box.remove();
          }
        }));
    }

    placeLevelBar(box, 0);
  }

  /* Sits just above the products: before the "25 Products Found" row, which
   * the theme draws directly over the grid. Falls back to the top of the page
   * only if that row never appears. */
  function levelAnchor() {
    /* Match on an element's whole text, not a single text node: the theme
     * puts the number in its own tag ("<span>30</span> Products Found"), so
     * no one text node ever contained the full phrase and the bar fell back
     * to the top of the page. Take the innermost visible match. */
    var RE = /\b\d+\s*products?\s+found\b/i;
    var hits = Array.prototype.filter.call(document.body.querySelectorAll("div, p, span, li, h2, h3, h4, h5, h6"), function (e) {
      return RE.test(e.textContent || "") && e.getClientRects().length && e.getBoundingClientRect().width > 0;
    });
    var el = hits.filter(function (e) { return !hits.some(function (o) { return o !== e && e.contains(o); }); })[0];
    if (!el) return null;
    // climb from the count to the row that spans the product area, stopping
    // before the container that also holds the page title
    while (el.parentElement && el.parentElement !== document.body &&
           !el.parentElement.querySelector("h1") &&
           el.getBoundingClientRect().width < el.parentElement.getBoundingClientRect().width * 0.6) {
      el = el.parentElement;
    }
    log("level bar anchored above:", el.className || el.tagName);
    return el;
  }

  /* Desktop: the row that holds the sort dropdown, found by climbing from the
   * dropdown until it spans the same width as the "Products Found" row. */
  function sortRow(ref) {
    var cands = document.querySelectorAll('[name="sort_by"], #SortBy, [id*="sort" i], [class*="sort" i]');
    var sort = Array.prototype.filter.call(cands, function (e) {
      return e.getClientRects().length && e.getBoundingClientRect().width > 0;
    })[0];
    if (!sort) return null;
    var want = ref ? ref.getBoundingClientRect().width * 0.9 : window.innerWidth * 0.5, el = sort;
    while (el.parentElement && el.parentElement !== document.body &&
           !el.parentElement.querySelector("h1") && el.getBoundingClientRect().width < want) {
      el = el.parentElement;
    }
    return el.getBoundingClientRect().width >= want && !(ref && el.contains(ref)) ? el : null;
  }

  function visibleSort() {
    var cands = document.querySelectorAll('[name="sort_by"], #SortBy, [id*="sort" i], [class*="sort" i]');
    return Array.prototype.filter.call(cands, function (e) {
      var r = e.getBoundingClientRect();
      return e.getClientRects().length && r.width > 40 && r.height > 20 && r.height < 90;
    })[0] || null;
  }

  function gridLeft() {
    var lefts = Array.prototype.map.call(document.querySelectorAll('main a[href*="/products/"], #MainContent a[href*="/products/"]'), function (l) {
      var r = l.getBoundingClientRect();
      return r.width > 60 ? r.left + window.scrollX : null;
    }).filter(function (x) { return x !== null; });
    return lefts.length ? Math.min.apply(null, lefts) : null;
  }

  // the full-width block that holds the product cards
  function gridBlock() {
    var link = Array.prototype.filter.call(document.querySelectorAll('main a[href*="/products/"], #MainContent a[href*="/products/"]'), function (l) {
      return l.getBoundingClientRect().width > 60;
    })[0];
    if (!link) return null;
    var el = link;
    while (el.parentElement && el.parentElement !== document.body && el.getBoundingClientRect().width < window.innerWidth * 0.8) {
      el = el.parentElement;
    }
    return el.getBoundingClientRect().width >= window.innerWidth * 0.8 ? el : null;
  }

  function floatOverGrid(box) {
    var sort = visibleSort(), left = gridLeft();
    if (!sort || left === null) return false;
    var place = function () {
      var s = sort.getBoundingClientRect();
      var right = s.left + window.scrollX - 16;
      var mid = (left + right) / 2;
      box.style.left = mid + "px";
      box.style.top = (s.top + window.scrollY + s.height / 2) + "px";
    };
    box.style.position = "absolute";
    box.style.transform = "translate(-50%, -50%)";
    box.style.width = "auto";
    box.style.minWidth = "380px";
    box.style.margin = "0";
    box.style.zIndex = "2";
    box.style.flexWrap = "nowrap";
    box.style.whiteSpace = "nowrap";
    box.style.justifyContent = "space-between";
    var m = box.querySelector(".ajg-bar__msg");
    if (m) { m.style.flex = "0 1 auto"; m.style.minWidth = "0"; }
    document.body.appendChild(box);
    place();
    window.addEventListener("resize", place);
    window.addEventListener("load", place);
    setTimeout(place, 800);
    log("level bar floated over the product grid, level with sort");
    return true;
  }

  function placeLevelBar(box, tries) {
    var at = levelAnchor();
    var wide = window.innerWidth >= 990;
    var row = wide ? sortRow(at) : null;
    if (wide && !row && !at && floatOverGrid(box)) return;
    if (wide && !row && tries < 16) { setTimeout(function () { placeLevelBar(box, tries + 1); }, 300); return; }
    if (row) {
      // centred in the sort row, over its empty middle, without touching the
      // theme's own layout of that row
      if (getComputedStyle(row).position === "static") row.style.position = "relative";
      box.style.position = "absolute";
      box.style.left = "50%";
      box.style.top = "50%";
      box.style.transform = "translate(-50%, -50%)";
      box.style.width = "auto";
      box.style.minWidth = "380px";
      box.style.margin = "0";
      box.style.zIndex = "2";
      box.style.flexWrap = "nowrap";
      box.style.whiteSpace = "nowrap";
      box.style.justifyContent = "space-between";
      var m = box.querySelector(".ajg-bar__msg");
      if (m) { m.style.flex = "0 1 auto"; m.style.minWidth = "0"; }
      row.appendChild(box);
      log("level bar centred in the sort row");
      return;
    }
    if (at && at.parentElement) { at.parentElement.insertBefore(box, at); return; }
    if (!wide) {
      var grid = gridBlock();
      if (grid && grid.parentElement) { grid.parentElement.insertBefore(box, grid); log("level bar placed above the product grid"); return; }
    }
    if (tries < 16) { setTimeout(function () { placeLevelBar(box, tries + 1); }, 300); return; }
    var host = document.querySelector("#main, main, [role='main']") || document.body;
    if (host.firstChild) host.insertBefore(box, host.firstChild); else host.appendChild(box);
  }

  // these are not sold by skill level, so there is nothing to filter by
  var NO_LEVEL = /\/collections\/(wedges|putters|chippers)(\/|$)/i;

  function runCollection() {
    if (!/\/collections\//.test(location.pathname)) return false;
    if (NO_LEVEL.test(location.pathname)) { log("collection: no skill levels here"); return true; }
    // and any other collection whose sidebar has no Skill Level filter
    if (!document.querySelector('[name="' + SKILL_FILTER + '"]')) { log("collection: no Skill Level filter on this page"); return true; }
    var data = readSaved();
    var score = data && data.clubs && Object.keys(data.clubs)
      .map(function (k) { return data.clubs[k].fit && data.clubs[k].fit.score; })
      .filter(Boolean)[0];
    if (!score || !SKILL_VALUE[score]) { log("collection: no saved level"); return true; }
    if (levelCleared()) { log("collection: skill level cleared for this fitting"); return true; }

    var url = new URL(location.href);
    var already = url.searchParams.getAll(SKILL_FILTER);
    var label = SKILL_VALUE[score];

    if (already.length) { levelBar(label); return true; }
    // any filtering of their own, or an opt-out, means hands off
    var theirs = false;
    url.searchParams.forEach(function (v, k) { if (/^filter\./.test(k)) theirs = true; });
    if (theirs || url.searchParams.get("ajgfit")) {
      log("collection: leaving their own filters alone");
      return true;
    }
    if (optedOut()) {
      log("collection: they chose to see every club; offering the filter instead");
      levelBar(label, true);
      return true;
    }
    url.searchParams.append(SKILL_FILTER, label);
    url.searchParams.append(SKILL_FILTER, SKILL_ALL);
    url.searchParams.set("ajgfit", "1");
    log("collection: filtering to", label);
    location.replace(url.toString());
    return true;
  }

  /* ---------- cart note ----------
   * "Notes for the builder" had nowhere to go. It now lands in the cart note,
   * which follows the order through to the packing slip.
   *
   * Two steps, because filling the visible textarea is cosmetic - the theme
   * only posts it on its own schedule, and the drawer may never post at all.
   * So we write it to the cart with /cart/update.js AND mirror it into the box
   * so the customer can see and edit it.
   *
   * Guards, since a note is the customer's own words:
   *   - never overwrite a note that differs from ours
   *   - write once per note per session, so an edit of ours stays edited
   *   - do nothing on an empty cart, or with no note saved  */
  var NOTE_DONE = "ajg_note_applied";

  function savedNote() {
    var d = readSaved();
    return d && typeof d.note === "string" && d.note.trim() ? d.note.trim() : "";
  }

  function noteAlreadyHandled(note) {
    try { return sessionStorage.getItem(NOTE_DONE) === note; } catch (e) { return false; }
  }

  function markNoteHandled(note) {
    try { sessionStorage.setItem(NOTE_DONE, note); } catch (e) {}
  }

  function noteBoxes() {
    return document.querySelectorAll(
      'textarea[name="note"], textarea[name="attributes[note]"], #CartNote, #cart-note, .cart-note__input'
    );
  }

  function fillNoteBoxes(note) {
    var filled = 0;
    Array.prototype.forEach.call(noteBoxes(), function (box) {
      var cur = (box.value || "").trim();
      if (cur === note) { filled++; return; }
      if (cur && note.indexOf(cur) !== 0) return;   // their words win
      box.value = note;
      fire(box);
      filled++;
    });
    return filled;
  }

  function applyCartNote() {
    var note = savedNote();
    if (!note || noteAlreadyHandled(note)) return;

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) { log("cart note: cart is empty"); return; }
        var cur = (cart.note || "").trim();
        var out = note;
        if (cur && cur !== note) {
          // already appended on an earlier pass - nothing to do
          if (cur.indexOf(note) >= 0) { markNoteHandled(cur); fillNoteBoxes(cur); return; }
          out = cur + "\n\n" + note;
          log("cart note: appending to the customer's own note");
        }
        if (cur === note) { markNoteHandled(note); fillNoteBoxes(note); return; }

        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note: out })
        }).then(function (r) {
          if (!r.ok) return;
          markNoteHandled(out);
          fillNoteBoxes(out);
          log("cart note written:", JSON.stringify(out.slice(0, 80)));
        });
      })
      .catch(function () {});
  }

  /* The drawer renders on demand and the cart page re-renders on quantity
   * changes, so watch for a note box appearing and mirror into it. */
  function watchNoteBoxes() {
    if (!savedNote()) return;
    var mo = new MutationObserver(function () {
      var note = savedNote();
      if (!note) return;
      if (!noteAlreadyHandled(note)) return;
      if (noteBoxes().length) fillNoteBoxes(note);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- single-item fittings ----------
   * The "Not sure? Click here for a quick fitting" links beside Shaft Flex,
   * Shaft Length / Driver Length (added by aj-custom.js), Grip Size and Loft
   * (added here) open the pop-up on just that one question. The answer is kept
   * in its own record, ajg_quick, per club type and per item - never in
   * ajg_fitting - so the shopper still counts as having no fitting: the invite
   * bar stays, collections are not filtered, nothing goes on the cart. Once an
   * item is done its link goes away and the value is filled on every product
   * page of that club type. A full fitting for the club replaces them all. */
  var QUICK_KEY = "ajg_quick";
  // which APO fields each single-item fitting may fill
  var QUICK_IDS = { flex: ["flex"], length: ["length", "lie"], grip: ["grip"], loft: ["loft"] };

  function readQuick() {
    try {
      var q = JSON.parse(window.localStorage.getItem(QUICK_KEY) || "null");
      return q && q.clubs ? q : { v: 1, clubs: {} };
    } catch (e) { return { v: 1, clubs: {} }; }
  }

  function writeQuick(q) {
    try { window.localStorage.setItem(QUICK_KEY, JSON.stringify(q)); } catch (e) {}
  }

  function dropQuick(club) {
    var q = readQuick();
    if (!q.clubs[club]) return;
    delete q.clubs[club];
    writeQuick(q);
    log("full fitting found; single-item results for", club, "removed");
  }

  function quickFor(club) {
    var items = readQuick().clubs[club];
    if (!items) return null;
    var fit = {}, only = [];
    Object.keys(items).forEach(function (it) {
      var v = items[it] || {};
      Object.keys(v).forEach(function (k) { if (k !== "at") fit[k] = v[k]; });
      only = only.concat(QUICK_IDS[it] || []);
    });
    return only.length ? { items: items, fit: fit, only: only } : null;
  }

  // a real fitting has more in it than a shaft picked on the shaft chart
  function fittedFor(club) {
    var d = readSaved();
    var c = d && d.clubs && d.clubs[club];
    if (!c) return false;
    return !!((c.fit && Object.keys(c.fit).some(function (k) { return k !== "shaft" && k !== "shaftPicked"; })) ||
      (c.specs && Object.keys(c.specs).length));
  }

  var quickObs = null, quickTimer = null, quickFilled = [], quickRefills = 0;
  // selects already filled once; after that only a reset to the placeholder is refilled
  var quickTouched = typeof WeakSet === "function" ? new WeakSet() : null;

  function quickCovers(id) {
    var club = pageClub();
    if (!club || fittedFor(club)) return false;
    var rec = quickFor(club);
    return !!(rec && rec.only.indexOf(id) >= 0);
  }

  function quickNeedsRefill() {
    for (var i = 0; i < quickFilled.length; i++) {
      var sel = quickFilled[i].select;
      if (!sel) continue;
      if (!document.contains(sel) || onPlaceholder(sel)) return true;
    }
    return false;
  }

  /* Fills only the fields the single-item fittings cover. Their own picks
   * still win: wantedFor() hands back anything they chose by hand. */
  function applyQuick(club) {
    if (!quickFor(club)) return;
    var pass = function () {
      if (!document.querySelector(".mw-apo-container select, .mw-option select")) return;
      // read fresh each time, so an item they clear stops being filled
      var rec = quickFor(club);
      if (!rec) return;
      gateCheckboxes(rec.fit, {}, rec.only);
      var a = apply(rec.fit, {}, rec.only, quickTouched);
      if (a.length) quickFilled = a.slice();
    };
    var until = Date.now() + WATCH_MS;
    if (quickObs) quickObs.disconnect();
    pass();
    quickObs = new MutationObserver(function () {
      clearTimeout(quickTimer);
      quickTimer = setTimeout(function () {
        // APO renders late, and a ticked lie box renders its own select after
        if (Date.now() < until) { pass(); return; }
        if (quickRefills > 40 || !quickNeedsRefill()) return;
        quickRefills++;
        log("options were reset; re-applying the single-item fittings");
        pass();
      }, SETTLE_MS + 150);
    });
    quickObs.observe(document.body, { childList: true, subtree: true });
  }

  function itemForTitle(t, club) {
    var n = cleanLabel(t).toLowerCase();
    if (/^shaft flex$/.test(n)) return club === "putter" ? "" : "flex";
    if (/^(shaft|driver) length$/.test(n)) return "length";
    if (/^grip size$/.test(n)) return club === "putter" ? "" : "grip";
    if (/^(driver )?loft$/.test(n)) return club === "driver" ? "loft" : "";
    return "";
  }

  /* Takes the place of "Not sure?" once that item is done. Styled from the
   * hidden "Not sure?" link beside it so the two look the same. */
  function addClearLink(label, item) {
    var a = document.createElement("a");
    a.href = "#";
    a.setAttribute("data-ajg-qclear", item);
    a.textContent = "Clear my fitting info";
    var twin = label.querySelector(".aj-fit-link");
    var css = "cursor:pointer;margin-left:8px;color:#ce2029;text-decoration:underline;";
    if (twin) {
      var cs = getComputedStyle(twin);
      css = "cursor:pointer;color:" + cs.color + ";font-size:" + cs.fontSize + ";font-weight:" + cs.fontWeight +
        ";font-style:" + cs.fontStyle + ";text-decoration:" + cs.textDecorationLine + ";margin-left:" +
        (cs.marginLeft === "0px" ? "8px" : cs.marginLeft) + ";";
      twin.parentNode.insertBefore(a, twin.nextSibling);
    } else {
      var ic = label.querySelector(".mw-tooltip--icon");
      if (ic) ic.parentNode.insertBefore(a, ic.nextSibling);
      else {
        var wrapItem = document.createElement("div");
        wrapItem.className = "mw-stack__item";
        wrapItem.appendChild(a);
        (label.querySelector(".mw-stack") || label).appendChild(wrapItem);
      }
    }
    a.style.cssText = css;
  }

  /* Forgets one single-item result and puts its fields back to APO's
   * default, whatever they are set to now. */
  function clearQuick(item) {
    var club = pageClub();
    if (!club) return;
    var ids = QUICK_IDS[item] || [];
    var q = readQuick();
    if (q.clubs[club]) {
      delete q.clubs[club][item];
      if (!Object.keys(q.clubs[club]).length) delete q.clubs[club];
      writeQuick(q);
    }
    ids.forEach(function (id) { delete userPicks[id]; });
    filling = true;
    /* Read from the page as it is now rather than from what was filled
     * earlier, since APO may have rebuilt the selects since then. Every
     * field the item covers goes back to APO's own default. */
    var root = document.querySelector(".mw-apo-container") || document.body;
    Array.prototype.forEach.call(root.querySelectorAll(".mw-option, .mw-option__container"), function (block) {
      var selects = block.querySelectorAll("select.mw-option__control, select");
      Array.prototype.forEach.call(selects, function (sel) {
        var m = classify(ownTitle(sel, block)) || (selects.length === 1 ? classify(titleOf(block)) : null);
        if (!m || ids.indexOf(m.id) < 0 || sel.disabled) return;
        var key = sel.name || sel.id;
        var opts = Array.prototype.slice.call(sel.options);
        var def = opts.filter(function (o) { return o.defaultSelected; })[0] ||
          (key && key in originals ? opts.filter(function (o) { return o.value === originals[key]; })[0] : null) ||
          opts.filter(function (o) { return o.value === "" || /please select/i.test(o.textContent); })[0] || opts[0];
        if (!def || def.value === sel.value) return;
        sel.value = def.value;
        fire(sel);
        log("cleared", m.id, "back to", JSON.stringify(norm(def.textContent)));
      });
    });
    quickFilled = quickFilled.filter(function (f) { return ids.indexOf(f.id) < 0; });
    // the lie box goes back to unticked too
    if (item === "length") {
      Array.prototype.forEach.call(document.querySelectorAll('.mw-option input[type="checkbox"]'), function (box) {
        if (!box.checked || !/\blie\b/.test(norm(labelForInput(box)))) return;
        var k = gateKey(box);
        if (k) delete userGates[k];
        box.checked = false;
        fire(box);
        try { box.dispatchEvent(new MouseEvent("click", { bubbles: true })); } catch (e) {}
        if (box.checked) { box.checked = false; fire(box); }
      });
    }
    filling = false;
    log("single-item fitting cleared:", club, item);
    updateQuickLinks();
  }

  /* aj-custom.js adds and removes the Shaft Flex and Length links; this adds
   * the Grip Size and Loft ones the same way, and hides any link whose item
   * is already done for this club type. */
  function updateQuickLinks() {
    if (!/\/products\//.test(location.pathname)) return;
    var club = pageClub();
    if (!club) return;
    if (!document.getElementById("ajg-qlink-css")) {
      var t = document.createElement("style");
      t.id = "ajg-qlink-css";
      t.textContent = ".mw-option[data-ajg-qdone] .aj-fit-link{display:none !important}";
      document.head.appendChild(t);
    }
    var fitted = fittedFor(club);
    var done = readQuick().clubs[club] || {};
    Array.prototype.forEach.call(document.querySelectorAll(".mw-option"), function (o) {
      var strong = o.querySelector("label .mw-text--style-strong");
      if (!strong) return;
      var item = itemForTitle(strong.textContent, club);
      if (!item) return;
      if (done[item]) { if (!o.hasAttribute("data-ajg-qdone")) o.setAttribute("data-ajg-qdone", "1"); }
      else if (o.hasAttribute("data-ajg-qdone")) o.removeAttribute("data-ajg-qdone");
      var lbl = strong.closest("label") || o;
      var clr = lbl.querySelector("[data-ajg-qclear]");
      if (done[item] && !fitted) {
        if (!clr) addClearLink(lbl, item);
      } else if (clr) clr.parentNode.removeChild(clr);
      if (item !== "grip" && item !== "loft") return;
      var label = strong.closest("label") || o;
      var link = label.querySelector(".aj-fit-link");
      if (fitted) { if (link) link.parentNode.removeChild(link); return; }
      if (link) return;
      link = document.createElement("a");
      link.className = "aj-fit-link";
      link.setAttribute("data-ajg-qlink", item);
      link.href = FITTING_PAGE;
      link.textContent = ">Not sure? Click here for a quick fitting<";
      var ic = label.querySelector(".mw-tooltip--icon");
      if (ic) ic.parentNode.insertBefore(link, ic.nextSibling);
      else {
        var wrapItem = document.createElement("div");
        wrapItem.className = "mw-stack__item";
        wrapItem.appendChild(link);
        (label.querySelector(".mw-stack") || label).appendChild(wrapItem);
      }
    });
  }

  function startQuickLinks() {
    if (!/\/products\//.test(location.pathname)) return;
    updateQuickLinks();
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(updateQuickLinks, 150);
    }).observe(document.body, { childList: true, subtree: true });
    var tries = 0, iv = setInterval(function () { updateQuickLinks(); if (++tries > 20) clearInterval(iv); }, 500);
  }

  /* Runs on the window in the capture phase, so it sees the click before
   * aj-custom.js does (that one listens on the document) and opens the
   * single-item pop-up instead of the full fitting. */
  window.addEventListener("click", function (e) {
    var c = e.target && e.target.closest && e.target.closest("[data-ajg-qclear]");
    if (c) {
      e.preventDefault();
      e.stopPropagation();
      clearQuick(c.getAttribute("data-ajg-qclear"));
      return;
    }
    var a = e.target && e.target.closest && e.target.closest(".aj-fit-link");
    if (!a || e.ctrlKey || e.metaKey || e.shiftKey || e.button > 0) return;
    if (!/\/products\//.test(location.pathname)) return;
    var o = a.closest(".mw-option");
    var strong = o && o.querySelector("label .mw-text--style-strong");
    var item = a.getAttribute("data-ajg-qlink") || (strong ? itemForTitle(strong.textContent, pageClub()) : "");
    if (!item) return;
    e.preventDefault();
    e.stopPropagation();
    openFittingModal(false, item);
  }, true);

  window.addEventListener("ajg:quick-saved", function (e) {
    var d = (e && e.detail) || {};
    var club = d.club || pageClub();
    if (!club || !QUICK_IDS[d.item] || !d.fit) return;
    var q = readQuick();
    var rec = {};
    Object.keys(d.fit).forEach(function (k) { rec[k] = d.fit[k]; });
    rec.at = new Date().toISOString();
    (q.clubs[club] = q.clubs[club] || {})[d.item] = rec;
    writeQuick(q);
    log("single-item fitting saved:", club, d.item, rec);
    // the fitting is newer than anything they picked by hand in that field
    QUICK_IDS[d.item].forEach(function (id) { delete userPicks[id]; });
    quickRefills = 0;
    // a new result is applied once even over a choice they made earlier
    if (quickTouched) quickTouched = new WeakSet();
    setTimeout(function () {
      if (window.__ajgCloseModal) window.__ajgCloseModal();
      if (!fittedFor(club)) applyQuick(club);
      updateQuickLinks();
      // bring the field they asked about back into view
      var hit = quickFilled.filter(function (f) { return f.id === QUICK_IDS[d.item][0]; })[0];
      var opt = hit && hit.select && hit.select.closest(".mw-option");
      if (opt) {
        try {
          var y = opt.getBoundingClientRect().top + window.pageYOffset - 140;
          window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
        } catch (err) {}
      }
    }, 900);
  }, false);

  window.addEventListener("ajg:fitting-saved", function () { setTimeout(updateQuickLinks, 300); }, false);

  /* ?fitting=open on a product URL opens the pop-up on arrival, for links in
   * emails and ads. Waits for the page to settle so the product options exist
   * behind it, and strips the parameter so a refresh does not reopen it. */
  function openFromLink() {
    var q = new URLSearchParams(location.search);
    var v = (q.get("fitting") || "").toLowerCase();
    if (v !== "open" && v !== "new") return;
    if (!/\/products\//.test(location.pathname)) return;
    try {
      q.delete("fitting");
      var qs = q.toString();
      history.replaceState(history.state, "", location.pathname + (qs ? "?" + qs : "") + location.hash);
    } catch (e) {}
    setTimeout(function () { openFittingModal(v === "new"); }, 900);
  }

  /* ---------- cart tracking ----------
   * When a fitted club lands in the cart we do two things:
   *   1. tag the cart with "Fitting ID", which Shopify carries through checkout
   *      onto the order, so an order can be matched to its row in the sheet
   *   2. tell the fitting log, which marks that row "In cart"
   * Each fitting is reported once per browser, and only for a club it was
   * actually fitted for. Must match LOG_URL / LOG_KEY in the guide. */
  var LOG_URL = "https://script.google.com/macros/s/AKfycbxxjp0fY1YTb_W0lc7IGk2zwm1QMAlXJvJXe0FX7Gx7xnfJG7zxrYu4LAGDCVFvIZI/exec";
  var LOG_KEY = "ajg-fit-2026";
  var CART_SENT = "ajg_cart_logged";

  function itemClub(item) {
    var hay = [item.product_title, item.title, item.handle, item.product_type].join(" ").toLowerCase();
    if (/chipper/.test(hay)) return "putter";
    var order = ["putter", "wedge", "hybrid", "irons", "wood", "driver"];
    for (var i = 0; i < order.length; i++) {
      var words = CLUB_WORDS[order[i]];
      for (var j = 0; j < words.length; j++) if (hay.indexOf(words[j]) >= 0) return order[i];
    }
    return null;
  }

  function sentMap() {
    try { return JSON.parse(localStorage.getItem(CART_SENT) || "{}"); } catch (e) { return {}; }
  }

  function trackCart() {
    var d = readSaved();
    if (!d || !d.clubs) return;
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        var hits = {};
        (cart.items || []).forEach(function (it) {
          var k = itemClub(it);
          var rec = k && d.clubs[k];
          if (!rec || !rec.fid) return;
          (hits[rec.fid] = hits[rec.fid] || { club: k, products: [] }).products.push(it.product_title);
        });
        var ids = Object.keys(hits);
        if (!ids.length) return;

        // 1. the order tag - only rewritten when it would change
        var tag = ids.join(", ");
        var attrs = cart.attributes || {};
        if (attrs["Fitting ID"] !== tag) {
          fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: { "Fitting ID": tag } })
          }).catch(function () {});
        }

        // 2. the sheet - once per fitting
        var sent = sentMap();
        ids.forEach(function (id) {
          var sig = hits[id].products.slice().sort().join("|");
          var prev = sent[id];
          if (prev && typeof prev === "object" && prev.sig === sig && Date.now() - prev.at < 6 * 3600 * 1000) return;
          var body = JSON.stringify({
            key: LOG_KEY, event: "cart", fittingId: id, club: hits[id].club,
            products: hits[id].products
          });
          try {
            fetch(LOG_URL, { method: "POST", mode: "no-cors", keepalive: true,
              headers: { "Content-Type": "text/plain" }, body: body });
            sent[id] = { sig: sig, at: Date.now() };
            log("cart: reported fitting", id);
          } catch (e) {}
        });
        try { localStorage.setItem(CART_SENT, JSON.stringify(sent)); } catch (e) {}
      })
      .catch(function () {});
  }

  function watchCartAdds() {
    if (window.__ajgCartWatch) return;
    window.__ajgCartWatch = true;
    var isAdd = function (u) { return /\/cart\/(add|change|update)(\.js)?/.test(String(u || "")); };
    // a slow add can take well over six seconds, so keep looking for ten
    var later = function () { [800, 2500, 5000, 7500, 10000].forEach(function (ms) { setTimeout(trackCart, ms); }); };

    if (window.fetch) {
      var f = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === "string" ? input : (input && input.url);
        var p = f.apply(this, arguments);
        // our own attribute write would loop back here, so skip update calls we make
        if (isAdd(url) && !/\/cart\/update/.test(String(url))) p.then(later, function () {});
        return p;
      };
    }
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, url) {
      if (isAdd(url) && !/\/cart\/update/.test(String(url))) this.addEventListener("load", later);
      return open.apply(this, arguments);
    };
    // a classic form post reloads the page, where boot() runs trackCart anyway
  }

  /* An email link carries ?ajgspecs=TOKEN. The lookup can take 15 seconds
   * while Google wakes the script, so it never holds the page up: the code is
   * stripped from the address bar, kept in storage, and fetched in the
   * background. If they move on first, the next page picks it up again. When
   * it lands, a product page fills itself straight away. A newer fitting
   * already in this browser wins. */
  var PENDING = "ajg_pending_specs";

  function importFromLink(next) {
    var q = new URLSearchParams(location.search);
    var tok = q.get("ajgspecs");
    var pending = null;
    try { pending = JSON.parse(localStorage.getItem(PENDING) || "null"); } catch (e) {}
    if (tok) {
      pending = { tok: tok, tries: 0 };
      try { localStorage.setItem(PENDING, JSON.stringify(pending)); } catch (e) {}
      q.delete("ajgspecs");
      var s = q.toString();
      try { history.replaceState(history.state, "", location.pathname + (s ? "?" + s : "") + location.hash); } catch (e) {}
    }
    next();
    if (!pending || !pending.tok) return;
    if (pending.tries >= 6) { try { localStorage.removeItem(PENDING); } catch (e) {} return; }
    pending.tries++;
    try { localStorage.setItem(PENDING, JSON.stringify(pending)); } catch (e) {}

    fetch(LOG_URL + "?specs=" + encodeURIComponent(pending.tok))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (rec) {
        if (!rec) return;
        try { localStorage.removeItem(PENDING); } catch (e) {}
        if (!rec.clubs || !Object.keys(rec.clubs).length) { log("email link: no fitting stored for that code"); return; }
        var mine = readSaved();
        var theirs = Date.parse(rec.saved || "") || 0;
        var local = mine ? (Date.parse(mine.saved || "") || 0) : 0;
        if (mine && theirs < local) { log("email link ignored: a newer fitting is saved here"); return; }
        writeBoth(rec);
        log("specs imported from email link");
        if (/\/products\//.test(location.pathname)) {
          stop(); stopGuard();
          done = false; observer = null; refills = 0;
          run();
        }
      })
      .catch(function () { log("email link lookup failed; will retry on the next page"); });
  }

  function boot() {
    importFromLink(bootNow);
  }

  function bootNow() {
    openFromLink();
    startQuickLinks();
    watchCartAdds();
    trackCart();
    document.addEventListener("change", recordUserPick, true);
    startPickGuard();
    // the note rides along wherever they are, so it is attached before checkout
    applyCartNote();
    watchNoteBoxes();
    document.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      // an add-to-cart turns an empty cart into one worth noting
      if (t.closest('[name="add"], .hdt-btn--addtocart, [data-add-to-cart]')) {
        setTimeout(applyCartNote, 1200);
        setTimeout(trackCart, 1600);
      }
    }, true);

    if (runCollection()) return;
    run();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();

/* ---------------------------------------------------------------------------
 * NOTES — built from the APO payload for the Acer SR1 Driver
 *
 * 1. OPTION TITLES IT RECOGNISES: "Driver Length" (and any *Length*), "Loft",
 *    "Shaft Flex", "Shaft Selection", "Grip Size", "Stance", plus "Lie" and
 *    "Bounce" on iron and wedge pages, and "Head Style" on putters. It
 *    deliberately ignores Grip Style,
 *    Grip Color, Shaft Spine Alignment and Add Head Cover.
 *
 * 2. LENGTH IS ABSOLUTE, NOT A DELTA. Your list reads 43"–47" while the guide
 *    recommends "+1/2"" against a 45" standard, so the guide now saves an
 *    absolute length too and this script picks the nearest option, saying
 *    "(closest match)" when it is not exact. Same for Loft: a driver
 *    offering only 9.5/10.5/12 gets the nearest to the recommendation.
 *
 * 3. FLEX NAMING IS ALIASED, AND NEVER APPROXIMATED UPWARDS. Senior, Lite,
 *    A-Flex and Amateur Flex are treated as one flex; Ladies is softer and is
 *    never used as a substitute. Extra Stiff prefers a genuine X option
 *    (irons stock it, woods will), and only falls back to Stiff when the
 *    product offers no X at all. Nothing else substitutes: a Stiff
 *    recommendation will never pick Extra Stiff. Matching is exact on the
 *    flex name, so a product with no suitable flex is left untouched and the
 *    customer sees the recommendation in the panel instead.
 *
 * 4. SHAFT NAMES MATCH LOOSELY. The guide says "Hypersonic SL50"; your list
 *    says "SK Fiber Hypersonic SL50". Containment matching handles that, and
 *    prefers the shortest matching option so plain "Hypersonic" does not grab
 *    the SL50 entry.
 *
 * 5. SAME ORIGIN IS REQUIRED. The saved specs live in a cookie and in
 *    localStorage on whichever domain wrote them. Paste the guide into a
 *    Shopify page and it shares averagejoesgolf.com with your product pages.
 *    Serve it from another host in an iframe and this finds nothing — ask and
 *    I will add a postMessage handoff for that case.
 *
 * 6. TUNING: ?ajgdebug=1 logs each option title, whether it was classified,
 *    and every candidate value when a match fails.
 * --------------------------------------------------------------------------- */
